import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Y from "yjs";
import { useParams } from "react-router-dom";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import BreakTimeTicTacToe, {
  BREAK_TIME_GAME_OPTIONS,
  createInitialBreakTimeState,
  normalizeBreakTimeState
} from "../components/BreakTimeTicTacToe";
import { FigureRender } from "../components/FigureRender";
import { formatLatexForDisplay } from "../lib/latexUtils";
import MathLiveEditor from "../components/MathLiveEditor";
import SlateCanvasPanel from "../components/SlateCanvasPanel";
import useInkDeviceProfile from "../hooks/useInkDeviceProfile";
import {
  createInkPointProcessor,
  getCoalescedPointerSamples
} from "../lib/inkDeviceProfile";
import { createInkPointerOwnership } from "../lib/inkPointerOwnership";
import {
  renderClusterImageData,
  updateStepClustersIncremental
} from "../lib/dynamicInkSegmenter";
import {
  appendCommittedStrokes,
  buildCollaborationWsUrl,
  clearSharedCanvas,
  createCollaborationDoc,
  readSharedBreakTimeState,
  readSharedAttemptState,
  readSharedCanvasState,
  readSharedEditorLatex,
  removeCommittedStroke,
  replaceLaneHeights,
  setSharedAttemptState,
  setSharedBreakTimeState,
  setSharedEditorLatex,
  setSharedPaperStyle
} from "../lib/collaborationYjsRoom";

const OCR_DELAY_MS = 820;
const OCR_MIN_DELAY_MS = 140;
const DEFAULT_LANE_HEIGHT = 90;
const MIN_LANE_HEIGHT = 56;
const MIN_STAGE_WIDTH = 220;
const INITIAL_LANE_COUNT = 10;
const LANE_BATCH = 8;
const LANE_PAD = 16;
const STAGE_RIGHT_PAD = 24;
const DIAGNOSTIC_RAIL_WIDTH = 40;
const RULE_STROKE = "rgba(37, 99, 235, 0.35)";
const RULE_STROKE_WIDTH = 1.2;
const RULE_DASH = [2, 7];



function stripMathDelimiters(line) {
  return String(line || "")
    .replace(/```[a-zA-Z]*\n?/g, "")
    .replace(/```/g, "")
    .replace(/\\\(/g, "")
    .replace(/\\\)/g, "")
    .replace(/\\\[/g, "")
    .replace(/\\\]/g, "")
    .replace(/\$/g, "")
    .trim();
}

function splitLatexSteps(input) {
  return String(input || "")
    .split(/\s*\\\\\s*|\n+|\\quad\s*/)
    .map((line) => stripMathDelimiters(line))
    .filter(Boolean)
    .filter((line) => !/^\\(begin|end)\{/.test(line));
}

function normalizeOcrLatexForEditor(input) {
  const envPattern =
    "(?:align\\*?|gather\\*?|aligned|equation\\*?|multline\\*?|cases|array|matrix|pmatrix|bmatrix|vmatrix|smallmatrix)";
  const value = String(input || "")
    .replace(new RegExp(`\\\\begin\\{${envPattern}\\}`, "g"), "")
    .replace(new RegExp(`\\\\end\\{${envPattern}\\}`, "g"), "")
    .replace(/&/g, "")
    .replace(/```[a-zA-Z]*\n?/g, "")
    .replace(/```/g, "")
    .replace(/\\\(/g, "")
    .replace(/\\\)/g, "")
    .replace(/\\\[/g, "")
    .replace(/\\\]/g, "")
    .replace(/\$/g, "")
    .trim();

  return splitLatexSteps(value).join(" \\\\ ");
}

function buildLaneHeights(count = INITIAL_LANE_COUNT) {
  return Array.from({ length: Math.max(2, Number(count) || INITIAL_LANE_COUNT) }, () => DEFAULT_LANE_HEIGHT);
}

function sanitizeLaneHeights(input) {
  const heights = Array.isArray(input)
    ? input
      .map((height) => Number(height))
      .filter((height) => Number.isFinite(height) && height >= MIN_LANE_HEIGHT && height <= 520)
      .slice(0, 80)
    : [];
  return heights.length ? heights : buildLaneHeights();
}

function serializeLaneHeights(input) {
  return JSON.stringify(sanitizeLaneHeights(input));
}

function getLaneBounds(heights) {
  let y = 0;
  return sanitizeLaneHeights(heights).map((h, index) => {
    const height = Math.max(MIN_LANE_HEIGHT, Number(h) || DEFAULT_LANE_HEIGHT);
    const top = y;
    const bottom = top + height;
    y = bottom;
    return { index, top, bottom, height };
  });
}

function getLaneIndexByY(y, lanes) {
  if (!lanes.length) return 0;
  const found = lanes.find((lane) => y >= lane.top && y <= lane.bottom);
  if (found) return found.index;
  return y < lanes[0].top ? 0 : lanes[lanes.length - 1].index;
}

function clampYToLane(y, lane) {
  if (!lane) return y;
  const min = lane.top + 10;
  const max = lane.bottom - 10;
  return min >= max ? (lane.top + lane.bottom) / 2 : Math.max(min, Math.min(max, y));
}

function getLineIdForLane(laneIndex) {
  return `lane_${Number(laneIndex) || 0}`;
}

function normalizeStrokes(input) {
  return (Array.isArray(input) ? input : [])
    .map((stroke, index) => {
      const rawPoints = Array.isArray(stroke?.points) ? stroke.points.map(Number).filter(Number.isFinite) : [];
      if (rawPoints.length < 2) return null;
      const points = rawPoints.length >= 4 ? rawPoints : [rawPoints[0], rawPoints[1], rawPoints[0] + 0.01, rawPoints[1] + 0.01];
      const tool = String(stroke?.tool || "pen") === "eraser" ? "eraser" : "pen";
      const strokeId = String(stroke?.strokeId || stroke?.id || `stroke_${index}`);
      return {
        ...stroke,
        id: strokeId,
        strokeId,
        userId: String(stroke?.userId || ""),
        tool,
        color: stroke?.color || "#1d4ed8",
        width: Number(stroke?.width) || (tool === "eraser" ? 28 : 2.5),
        laneIndex: Number.isFinite(Number(stroke?.laneIndex)) ? Number(stroke.laneIndex) : null,
        points
      };
    })
    .filter(Boolean);
}

function getStatusMeta(status) {
  if (status === "incorrect_but_right_direction") return { label: "Fix", className: "status-incomplete" };
  if (status === "wrong_direction") return { label: "Off path", className: "status-incorrect" };
  if (status === "dependent_on_error") return { label: "Blocked", className: "status-review" };
  if (status === "ahead_of_frontier") return { label: "Later", className: "status-unclear" };
  if (status === "superseded") return { label: "Replaced", className: "status-review" };
  if (status === "incorrect") return { label: "Incorrect", className: "status-incorrect" };
  if (status === "incomplete") return { label: "Incomplete", className: "status-incomplete" };
  if (status === "missing_step") return { label: "Missing", className: "status-missing" };
  if (status === "unclear") return { label: "Unclear", className: "status-unclear" };
  if (status === "correct") return { label: "Correct", className: "status-correct" };
  return { label: "Review", className: "status-review" };
}

function getTutorModeMeta(mode) {
  if (mode === "redirect") return { label: "Course correction", className: "mode-redirect" };
  if (mode === "diagnostic_probe") return { label: "Diagnostic", className: "mode-diagnostic_probe" };
  if (mode === "celebrate") return { label: "Complete", className: "mode-celebrate" };
  if (mode === "entry_nudge") return { label: "Start", className: "mode-entry_nudge" };
  if (mode === "info") return { label: "Info", className: "mode-info" };
  return { label: "Next hint", className: "mode-next_step_hint" };
}

function getDiagramSummaryText(summary) {
  if (!summary) return "";
  if (typeof summary === "string") return summary.trim();
  if (summary.plainText) return String(summary.plainText).trim();
  const parts = [];
  const pushGroup = (label, values) => {
    if (!Array.isArray(values) || !values.length) return;
    parts.push(`${label}: ${values.slice(0, 6).join("; ")}`);
  };
  pushGroup("Relations", summary.relationSummary);
  pushGroup("Givens", summary.givenSummary);
  pushGroup("Dependencies", summary.dependencySummary);
  pushGroup("Entities", summary.entitySummary);
  return parts.join("\n").trim();
}

function getOcrLaneOrder(line, fallbackIndex = 0) {
  const explicit = Number(line?.lineOrder);
  if (Number.isFinite(explicit)) return explicit;
  const match = String(line?.lineId || "").match(/lane_(\d+)/i);
  if (match) {
    const parsed = Number(match[1]);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallbackIndex;
}

function normalizeOcrLaneRowsFromAttempt(attempt) {
  return (Array.isArray(attempt?.ocrLiveLines) ? attempt.ocrLiveLines : [])
    .map((line, index) => {
      const latex = String(line?.latex || "").trim();
      const diagramSummaryText = getDiagramSummaryText(line?.diagramSummary || null);
      const rawKind = String(line?.contentKind || "").trim().toLowerCase();
      const contentKind = ["math", "diagram", "mixed", "blank", "unclear"].includes(rawKind)
        ? rawKind
        : diagramSummaryText
          ? (latex ? "mixed" : "diagram")
          : latex
            ? "math"
            : "blank";
      return {
        lineId: String(line?.lineId || `lane_${index}`),
        lineOrder: getOcrLaneOrder(line, index),
        contentKind,
        latex,
        diagramSummaryText,
        confidence: String(line?.confidence || "").trim().toLowerCase()
      };
    })
    .filter((row) => row.contentKind !== "blank")
    .filter((row) => row.latex || row.diagramSummaryText || row.contentKind === "unclear")
    .sort((a, b) => {
      if (a.lineOrder !== b.lineOrder) return a.lineOrder - b.lineOrder;
      return a.lineId.localeCompare(b.lineId);
    });
}

function getOcrLaneKindLabel(kind) {
  if (kind === "diagram") return "Diagram understood";
  if (kind === "mixed") return "Math + diagram";
  if (kind === "unclear") return "Unclear";
  return "Math";
}

function clampVoiceLevel(value) {
  return Math.max(0, Math.min(1, Number(value) || 0));
}

function VoiceMeterBars({ level = 0, active = false, compact = false }) {
  const normalized = clampVoiceLevel(level);
  const thresholds = compact ? [0.08, 0.22, 0.4] : [0.08, 0.2, 0.34, 0.52];
  return (
    <span className={`collab-voice-meter ${compact ? "compact" : ""} ${active ? "active" : ""}`.trim()} aria-hidden="true">
      {thresholds.map((threshold, index) => (
        <span
          key={`${threshold}-${index}`}
          className={`collab-voice-meter-bar ${normalized >= threshold ? "filled" : ""}`.trim()}
        />
      ))}
    </span>
  );
}

function CollabHeaderIcon({ kind }) {
  if (kind === "break") {
    return (
      <svg viewBox="0 0 24 24" className="collab-header-icon-svg" aria-hidden="true">
        <path d="M8.5 5v14M15.5 5v14M5 8.5h14M5 15.5h14" fill="none" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" />
        <path d="M6.9 6.9c1.2 0 2.2 1 2.2 2.2s-1 2.2-2.2 2.2-2.2-1-2.2-2.2 1-2.2 2.2-2.2Z" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M17.3 13.2 20 15.9M20 13.2l-2.7 2.7" fill="none" stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === "mic-off") {
    return (
      <svg viewBox="0 0 24 24" className="collab-header-icon-svg" aria-hidden="true">
        <path d="M12 15a3 3 0 0 0 3-3V7a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path d="M6 11a6 6 0 0 0 10.2 4.2M18 11a5.95 5.95 0 0 1-.88 3.11M12 17v3M9 20h6M4 4l16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === "phone") {
    return (
      <svg viewBox="0 0 24 24" className="collab-header-icon-svg" aria-hidden="true">
        <path d="M4.5 10.8c2.3-2.2 5-3.3 7.5-3.3s5.2 1.1 7.5 3.3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M6.2 10.2 4.7 14a1 1 0 0 0 .94 1.36h2.62a1 1 0 0 0 .9-.56l1.06-2.13M17.8 10.2 19.3 14a1 1 0 0 1-.94 1.36h-2.62a1 1 0 0 1-.9-.56l-1.06-2.13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === "copy") {
    return (
      <svg viewBox="0 0 24 24" className="collab-header-icon-svg" aria-hidden="true">
        <rect x="9" y="7" width="10" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path d="M7 15H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="collab-header-icon-svg" aria-hidden="true">
      <path d="M12 15a3 3 0 0 0 3-3V7a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M6 11a6 6 0 0 0 12 0M12 17v3M9 20h6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function getInkLaneMap(sourceLines, laneHasInk) {
  return [...new Set((Array.isArray(sourceLines) ? sourceLines : []).map((stroke) => Number(stroke?.laneIndex)))]
    .filter((idx) => Number.isFinite(idx) && idx >= 0)
    .sort((a, b) => a - b)
    .filter((idx) => (typeof laneHasInk === "function" ? laneHasInk(idx) : true));
}

function buildMathLaneMapFromAttempt(attempt, fallbackLines, laneHasInk) {
  const liveLines = Array.isArray(attempt?.ocrLiveLines) ? attempt.ocrLiveLines : [];
  const map = liveLines
    .map((line) => ({
      lineOrder: Number(line?.lineOrder),
      contentKind: String(line?.contentKind || "").trim().toLowerCase(),
      latex: String(line?.latex || "").trim()
    }))
    .filter((line) => Number.isFinite(line.lineOrder) && line.lineOrder >= 0)
    .filter((line) => line.latex && !/\[unclear\]/i.test(line.latex))
    .filter((line) => !["diagram", "blank", "unclear"].includes(line.contentKind))
    .sort((a, b) => a.lineOrder - b.lineOrder)
    .map((line) => line.lineOrder);

  return map.length ? [...new Set(map)] : getInkLaneMap(fallbackLines, laneHasInk);
}

function parseAnalysisFeedback(rawFeedback) {
  const source = String(rawFeedback || "").trim();
  if (!source) return null;
  try {
    if (source.startsWith("{")) return JSON.parse(source);
    const jsonStart = source.indexOf("{");
    const jsonEnd = source.lastIndexOf("}");
    if (jsonStart !== -1 && jsonEnd !== -1) {
      return JSON.parse(source.slice(jsonStart, jsonEnd + 1));
    }
  } catch (err) {
    return null;
  }
  return null;
}

function CollaborationPractice({ apiBase, token, onPageBusyChange }) {
  const { roomToken } = useParams();
  const {
    presentationProfile: profile,
    runtimeSettings: profileConfig
  } = useInkDeviceProfile({ apiBase, token });

  const stageRef = useRef(null);
  const slateRef = useRef(null);
  const canvasPaneRef = useRef(null);
  const toolbarMenuRef = useRef(null);
  const activeStrokeCanvasRef = useRef(null);
  const diagnosticRailRef = useRef(null);
  const renderCanvasRef = useRef(null);
  const cropCanvasRef = useRef(null);
  const linesRef = useRef([]);
  const laneHeightsRef = useRef(buildLaneHeights());
  const stageMetricsRef = useRef({ width: 0, height: 0 });
  const drawingRef = useRef(false);
  const pointerIdRef = useRef(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const hasToken = Boolean(token);
  const pointerOwnershipRef = useRef(createInkPointerOwnership(profileConfig));
  const activeLaneRef = useRef(null);
  const activeStrokeMetaRef = useRef(null);
  const activeStrokePointsRef = useRef([]);
  const activeStrokePointProcessorRef = useRef(null);
  const laneDragRef = useRef(null);
  const laneSyncTimerRef = useRef(null);
  const ocrTimerRef = useRef(null);
  const ocrBusyRef = useRef(false);
  const ocrFrozenRef = useRef(false);
  const pendingLinesRef = useRef(new Map());
  const pendingOrderRef = useRef([]);
  const laneRevisionRef = useRef({});
  const laneAckRevisionRef = useRef({});
  const laneInkStateRef = useRef({});
  const ocrEditedLatexRef = useRef("");
  const ocrEditorDirtyRef = useRef(false);
  const lastServerOcrLatexRef = useRef("");
  const editorSyncTimerRef = useRef(null);
  const applyAttemptStateRef = useRef(null);
  const tutorHintEntryIdRef = useRef(0);
  const pendingCanvasOpsRef = useRef(0);
  const laneLocalChangeRef = useRef(false);
  const lastLaneSyncKeyRef = useRef(serializeLaneHeights(buildLaneHeights()));
  const lastRevisionRef = useRef(null);
  const lastAttemptSyncAtRef = useRef(0);
  const attemptRef = useRef(null);
  const roomDocRef = useRef(null);
  const roomSocketRef = useRef(null);
  const roomSocketConnectedRef = useRef(false);
  const roomDocObserverRef = useRef(null);
  const roomDocSenderRef = useRef(null);
  const roomReconnectTimerRef = useRef(null);
  const roomReconnectAttemptRef = useRef(0);
  const roomSocketEnabledRef = useRef(false);
  const roomPollTimerRef = useRef(null);
  const roomPollBusyRef = useRef(false);
  const remotePreviewRef = useRef({});
  const previewFrameRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const voicePeerRef = useRef(null);
  const voiceLocalStreamRef = useRef(null);
  const voiceRemoteStreamRef = useRef(null);
  const voiceIceServersRef = useRef([]);
  const voicePendingCandidatesRef = useRef([]);
  const voiceMakingOfferRef = useRef(false);
  const voiceIgnoreOfferRef = useRef(false);
  const voiceSettingRemoteAnswerPendingRef = useRef(false);
  const voiceJoinedRef = useRef(false);
  const voiceMutedRef = useRef(false);
  const currentUserIdRef = useRef("");
  const remoteParticipantIdRef = useRef("");
  const isVoicePoliteRef = useRef(false);
  const voiceParticipantStatesRef = useRef({});
  const voiceAudioContextRef = useRef(null);
  const localVoiceAnalyserRef = useRef(null);
  const remoteVoiceAnalyserRef = useRef(null);
  const voiceMeterFrameRef = useRef(null);
  const voiceMeterCommitRef = useRef({ at: 0, local: 0, remote: 0 });
  const breakTimeStateKeyRef = useRef(JSON.stringify(normalizeBreakTimeState(createInitialBreakTimeState())));
  const breakTimeMenuRef = useRef(null);

  const [session, setSession] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [frame, setFrame] = useState(null);
  const [question, setQuestion] = useState(null);
  const [lines, setLines] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [laneHeights, setLaneHeights] = useState(() => buildLaneHeights());
  const [laneDragBoundary, setLaneDragBoundary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [liveOcrStatus, setLiveOcrStatus] = useState("Shared slate ready");
  const [ocrLaneRows, setOcrLaneRows] = useState([]);
  const [ocrEditedLatex, setOcrEditedLatex] = useState("");
  const [ocrReady, setOcrReady] = useState(false);
  const [showMathKeyboard, setShowMathKeyboard] = useState(false);
  const [strokeTool, setStrokeTool] = useState("pen");
  const [strokeColor, setStrokeColor] = useState("#1d4ed8");
  const [showPenMenu, setShowPenMenu] = useState(false);
  const [slateFullScreen, setSlateFullScreen] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [tutorChatOpen, setTutorChatOpen] = useState(false);
  const [tutorHintData, setTutorHintData] = useState(null);
  const [hintingIntent, setHintingIntent] = useState(null);
  const [tutorHintHistory, setTutorHintHistory] = useState([]);
  const [tutorLaneIndex, setTutorLaneIndex] = useState(0);
  const [analysisStatus, setAnalysisStatus] = useState("");
  const [analysisFeedback, setAnalysisFeedback] = useState("");
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisAlignment, setAnalysisAlignment] = useState([]);
  const [analysisFinalCorrect, setAnalysisFinalCorrect] = useState(null);
  const [analysisLaneMap, setAnalysisLaneMap] = useState([]);
  const [selectedDiagStep, setSelectedDiagStep] = useState(null);
  const [workTab, setWorkTab] = useState("slate");
  const [stepClusters, setStepClusters] = useState([]);
  const stepClustersRef = useRef([]);
  const clusterFingerprintsRef = useRef({});
  const clusterDebounceTimersRef = useRef(new Map());
  const previousMathClustersCountRef = useRef(0);

  const [paperStyle, setPaperStyle] = useState(() => {
    try {
      return localStorage.getItem("inksolver_slate_paper_style") || "white";
    } catch (_) {
      return "white";
    }
  });
  const paperStyleRef = useRef(paperStyle);
  paperStyleRef.current = paperStyle;

  const handleSetPaperStyle = useCallback((style) => {
    setPaperStyle(style);
    paperStyleRef.current = style;
    try {
      localStorage.setItem("inksolver_slate_paper_style", style);
    } catch (_) {}
    if (roomDocRef.current) {
      setSharedPaperStyle(roomDocRef.current, style);
    }
  }, []);

  const [stageWidth, setStageWidth] = useState(() => (typeof window === "undefined" ? 1120 : Math.max(420, window.innerWidth - 120)));
  const [roomSocketConnected, setRoomSocketConnected] = useState(false);
  const [remotePreviewLines, setRemotePreviewLines] = useState([]);
  const [voiceJoined, setVoiceJoined] = useState(false);
  const [voiceMuted, setVoiceMuted] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState("Voice off");
  const [voiceError, setVoiceError] = useState("");
  const [voiceParticipantStates, setVoiceParticipantStates] = useState({});
  const [localVoiceLevel, setLocalVoiceLevel] = useState(0);
  const [remoteVoiceLevel, setRemoteVoiceLevel] = useState(0);
  const [breakTimeOpen, setBreakTimeOpen] = useState(false);
  const [breakTimeMenuOpen, setBreakTimeMenuOpen] = useState(false);
  const [breakTimeState, setBreakTimeState] = useState(() => createInitialBreakTimeState());
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);

  const laneBounds = useMemo(() => getLaneBounds(laneHeights), [laneHeights]);
  const stageHeight = laneBounds.length ? laneBounds[laneBounds.length - 1].bottom : DEFAULT_LANE_HEIGHT * INITIAL_LANE_COUNT;
  const stageCanvasWidth = Math.max(360, stageWidth - STAGE_RIGHT_PAD);
  const stageSize = useMemo(() => ({ width: stageCanvasWidth, height: stageHeight }), [stageCanvasWidth, stageHeight]);
  const questionLatex = String(question?.latexRendered || question?.latex || attempt?.questionLatex || "");
  const questionFigure = question?.figureRenderSpec || question?.figureSpec || null;
  const showQuestionFigure = Boolean(questionFigure);
  const inviteUrl =
    typeof window === "undefined" || !roomToken ? "" : `${window.location.origin}/collaborate/${roomToken}`;
  const currentAttemptId = String(attempt?._id || session?.attemptId || "");
  const currentUserId = String(session?.currentParticipant?.userId || "");
  const isAnalyzing = busyAction === "analyze";
  const isHinting = busyAction === "hint";
  const otherParticipants = useMemo(
    () =>
      (Array.isArray(session?.participants) ? session.participants : []).filter(
        (participant) => String(participant?.userId || "") !== currentUserId
      ),
    [currentUserId, session?.participants]
  );
  const remoteParticipant = otherParticipants[0] || null;
  const remoteParticipantId = String(remoteParticipant?.userId || "");
  const isSecureVoiceContext = typeof window === "undefined" ? false : Boolean(window.isSecureContext);
  pointerOwnershipRef.current.setSettings(profileConfig);
  const canUseVoice =
    typeof window !== "undefined" &&
    typeof window.RTCPeerConnection === "function" &&
    typeof window.MediaStream === "function" &&
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia;
  const isVoicePolite = String(session?.ownerUserId || "") !== currentUserId;
  const localSpeaking = voiceJoined && !voiceMuted && localVoiceLevel >= 0.22;
  const normalizedBreakTimeState = useMemo(() => normalizeBreakTimeState(breakTimeState), [breakTimeState]);
  const activeBreakTimeGame =
    BREAK_TIME_GAME_OPTIONS.find((option) => option.id === normalizedBreakTimeState.game) ||
    BREAK_TIME_GAME_OPTIONS[0];
  const breakTimeStatusLabel = activeBreakTimeGame?.label || "Choose game";

  const tutorStepDiagnostics = useMemo(() => {
    const map = {};
    (Array.isArray(tutorHintData?.stepLedger) ? tutorHintData.stepLedger : []).forEach((item) => {
      const stepNo = Number(item?.studentStep);
      if (!Number.isFinite(stepNo) || stepNo <= 0) return;
      map[stepNo] = {
        status: item.status || "review",
        reason: item.reason || ""
      };
    });
    return map;
  }, [tutorHintData]);

  const showSlateDiagnostics =
    (analysisStatus === "Analysis complete" && analysisAlignment.length > 0) ||
    Object.keys(tutorStepDiagnostics).length > 0;
  const tutorModeMeta = useMemo(() => getTutorModeMeta(String(tutorHintData?.mode || "")), [tutorHintData]);

  const stepDiagnostics = useMemo(() => {
    const map = { ...tutorStepDiagnostics };
    analysisAlignment.forEach((item) => {
      const stepNo = Number(item?.student_step);
      if (!Number.isFinite(stepNo) || stepNo <= 0) return;
      map[stepNo] = {
        status: item.status || "review",
        reason: item.reason || ""
      };
    });
    return map;
  }, [analysisAlignment, tutorStepDiagnostics]);

  const slateRowDiagnostics = useMemo(() => {
    if (!showSlateDiagnostics) return [];
    return Object.keys(stepDiagnostics)
      .map((key) => Number(key))
      .filter((stepNo) => Number.isFinite(stepNo) && stepNo > 0)
      .sort((a, b) => a - b)
      .map((stepNo) => {
        const diagnostic = stepDiagnostics[stepNo] || null;
        const laneIndex = analysisLaneMap.length >= stepNo ? analysisLaneMap[stepNo - 1] : stepNo - 1;
        return {
          laneIndex,
          stepNo,
          diagnostic,
          statusMeta: getStatusMeta(diagnostic?.status)
        };
      });
  }, [analysisLaneMap, showSlateDiagnostics, stepDiagnostics]);

  const hasEditableOcrMath = Boolean(String(ocrEditedLatex || "").trim());
  const visibleOcrLaneRows = useMemo(() => {
    if (!hasEditableOcrMath) return ocrLaneRows;
    return ocrLaneRows.filter((row) => row.contentKind !== "math");
  }, [hasEditableOcrMath, ocrLaneRows]);
  const hasVisibleOcrLaneRows = visibleOcrLaneRows.length > 0;
  const syncLabel = session?.canvasState?.updatedAt
    ? new Date(session.canvasState.updatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";
  const voiceToggleIcon = !voiceJoined ? "mic" : voiceMuted ? "mic-off" : "mic";
  const voiceToggleLabel = !voiceJoined ? "Join voice" : voiceMuted ? "Unmute microphone" : "Mute microphone";
  const roomStatusLabel = pendingCanvasOpsRef.current > 0 ? "Sync" : roomSocketConnected ? (syncLabel || "Live") : "Wait";
  const roomStatusTitle = pendingCanvasOpsRef.current > 0
    ? "Syncing room changes..."
    : roomSocketConnected
      ? (syncLabel ? `Room live. Updated ${syncLabel}.` : "Room live.")
      : "Connecting room...";

  useEffect(() => {
    attemptRef.current = attempt;
    const updatedAtMs = attempt?.updatedAt ? new Date(attempt.updatedAt).getTime() : 0;
    if (updatedAtMs > 0) {
      lastAttemptSyncAtRef.current = Math.max(lastAttemptSyncAtRef.current, updatedAtMs);
    }
  }, [attempt]);

  useEffect(() => {
    const timers = clusterDebounceTimersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  useEffect(() => {
    linesRef.current = lines;
  }, [lines]);

  useEffect(() => {
    laneHeightsRef.current = laneHeights;
  }, [laneHeights]);

  useEffect(() => {
    stageMetricsRef.current = { width: stageCanvasWidth, height: stageHeight };
  }, [stageCanvasWidth, stageHeight]);

  useEffect(() => {
    drawingRef.current = isDrawing;
  }, [isDrawing]);

  useEffect(() => {
    ocrEditedLatexRef.current = ocrEditedLatex;
  }, [ocrEditedLatex]);

  useEffect(() => {
    voiceJoinedRef.current = voiceJoined;
  }, [voiceJoined]);

  useEffect(() => {
    voiceMutedRef.current = voiceMuted;
  }, [voiceMuted]);

  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  useEffect(() => {
    remoteParticipantIdRef.current = remoteParticipantId;
  }, [remoteParticipantId]);

  useEffect(() => {
    isVoicePoliteRef.current = isVoicePolite;
  }, [isVoicePolite]);

  useEffect(() => {
    setVoiceParticipantStates((prev) => {
      const allowedIds = new Set(otherParticipants.map((participant) => String(participant?.userId || "")).filter(Boolean));
      let changed = false;
      const next = {};
      Object.keys(prev || {}).forEach((userId) => {
        if (!allowedIds.has(userId)) {
          changed = true;
          return;
        }
        next[userId] = prev[userId];
      });
      return changed ? next : prev;
    });
  }, [otherParticipants]);

  useEffect(() => {
    voiceParticipantStatesRef.current = voiceParticipantStates;
  }, [voiceParticipantStates]);

  useEffect(() => {
    if (remoteParticipantId) return;
    setRemoteVoiceLevel(0);
  }, [remoteParticipantId]);

  useEffect(() => {
    if (typeof onPageBusyChange === "function") {
      onPageBusyChange("collaboration", loading, "Loading collaboration room...");
    }
    return () => {
      if (typeof onPageBusyChange === "function") {
        onPageBusyChange("collaboration", false);
      }
    };
  }, [loading, onPageBusyChange]);

  useEffect(() => {
    const initialState = createInitialBreakTimeState();
    breakTimeStateKeyRef.current = JSON.stringify(normalizeBreakTimeState(initialState));
    setBreakTimeState(initialState);
    setBreakTimeOpen(false);
    setBreakTimeMenuOpen(false);
  }, [roomToken]);

  useEffect(() => {
    if (!breakTimeMenuOpen) return undefined;
    const handleOutsidePointerDown = (event) => {
      const wrap = breakTimeMenuRef.current;
      if (!wrap || wrap.contains(event.target)) return;
      setBreakTimeMenuOpen(false);
    };
    document.addEventListener("pointerdown", handleOutsidePointerDown);
    return () => document.removeEventListener("pointerdown", handleOutsidePointerDown);
  }, [breakTimeMenuOpen]);

  useEffect(() => {
    function resize() {
      const host = canvasPaneRef.current?.closest?.(".collaboration-room-slate");
      setStageWidth(Math.max(420, Math.min(1800, Number(host?.clientWidth || window.innerWidth || 1200) - 34)));
    }
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  useEffect(() => {
    function handleEscape(event) {
      if (event.key === "Escape") {
        setSlateFullScreen(false);
        setShowClearConfirm(false);
        setShowAttachmentModal(false);
      }
    }
    if (slateFullScreen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [slateFullScreen]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!showPenMenu) return;
      const host = toolbarMenuRef.current;
      if (host && !host.contains(event.target)) {
        setShowPenMenu(false);
      }
    }
    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, [showPenMenu]);

  useEffect(() => {
    const pointerOwnership = pointerOwnershipRef.current;
    return () => {
      pointerOwnership.reset();
      if (ocrTimerRef.current) clearTimeout(ocrTimerRef.current);
      if (laneSyncTimerRef.current) clearTimeout(laneSyncTimerRef.current);
      if (editorSyncTimerRef.current) clearTimeout(editorSyncTimerRef.current);
      stopVoiceMeterLoop();
      clearVoiceAnalyser(localVoiceAnalyserRef);
      clearVoiceAnalyser(remoteVoiceAnalyserRef);
      const context = voiceAudioContextRef.current;
      voiceAudioContextRef.current = null;
      if (context && context.state !== "closed") {
        void context.close().catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    if (!showSlateDiagnostics) {
      setSelectedDiagStep(null);
      return;
    }
    if (selectedDiagStep === null) return;
    const exists = slateRowDiagnostics.some((item) => Number(item.stepNo) === Number(selectedDiagStep));
    if (!exists) setSelectedDiagStep(null);
  }, [selectedDiagStep, showSlateDiagnostics, slateRowDiagnostics]);

  useEffect(() => {
    if (!showSlateDiagnostics) return undefined;
    const handleOutsidePointerDown = (event) => {
      const railEl = diagnosticRailRef.current;
      if (!railEl) return;
      if (railEl.contains(event.target)) return;
      setSelectedDiagStep(null);
    };
    document.addEventListener("pointerdown", handleOutsidePointerDown);
    return () => document.removeEventListener("pointerdown", handleOutsidePointerDown);
  }, [showSlateDiagnostics]);

  function beginCanvasSync() {
    pendingCanvasOpsRef.current += 1;
  }

  function endCanvasSync() {
    pendingCanvasOpsRef.current = Math.max(0, pendingCanvasOpsRef.current - 1);
  }

  function clearTutorHint() {
    setTutorHintData(null);
  }

  function rememberTutorHint(status, tutor, options = {}) {
    tutorHintEntryIdRef.current += 1;
    const intent = typeof options === "string" ? options : (options?.intent || tutor?.intent || "next_step");
    setTutorHintHistory((prev) => [
      ...prev,
      {
        id: tutorHintEntryIdRef.current,
        status: String(status || "Hint"),
        createdAt: Date.now(),
        tutor: tutor ? { ...tutor, intent } : null,
        intent
      }
    ].slice(-12));
  }

  function updateOcrEditor(nextValue, options = {}) {
    const next = normalizeOcrLatexForEditor(nextValue || "");
    const current = normalizeOcrLatexForEditor(ocrEditedLatexRef.current || "");
    ocrEditedLatexRef.current = next;
    if (options.fromServer) {
      lastServerOcrLatexRef.current = next;
      ocrEditorDirtyRef.current = false;
    } else {
      ocrEditorDirtyRef.current = next !== lastServerOcrLatexRef.current;
    }
    if (next !== current) {
      setOcrEditedLatex(next);
    }
    setOcrReady(Boolean(next.trim()));
    if (!options.fromShared && roomDocRef.current) {
      const sharedCurrent = normalizeOcrLatexForEditor(readSharedEditorLatex(roomDocRef.current) || "");
      if (sharedCurrent !== next) {
        setSharedEditorLatex(roomDocRef.current, next);
      }
    }
  }

  function syncBreakTimeState(nextState) {
    const normalizedState = normalizeBreakTimeState(nextState);
    const nextKey = JSON.stringify(normalizedState);
    if (nextKey === breakTimeStateKeyRef.current) return;
    breakTimeStateKeyRef.current = nextKey;
    setBreakTimeState(normalizedState);
    if (roomDocRef.current) {
      setSharedBreakTimeState(roomDocRef.current, normalizedState);
    }
  }

  function handleOpenBreakTimeGame(gameId) {
    syncBreakTimeState({
      ...normalizedBreakTimeState,
      game: String(gameId || normalizedBreakTimeState.game || BREAK_TIME_GAME_OPTIONS[0]?.id || "tic_tac_toe"),
      updatedAt: new Date().toISOString()
    });
    setBreakTimeMenuOpen(false);
    setBreakTimeOpen(true);
  }

  function buildSharedAttemptState(nextAttempt) {
    if (!nextAttempt || typeof nextAttempt !== "object") return null;
    const hasField = (key) => Object.prototype.hasOwnProperty.call(nextAttempt, key);
    const nextEditedLatexSource = hasField("ocrEditedLatex")
      ? nextAttempt.ocrEditedLatex
      : ocrEditedLatexRef.current || "";
    const nextStatus = hasField("status")
      ? String(nextAttempt.status || "pending")
      : String(attemptRef.current?.status || "pending");
    return {
      _id: nextAttempt._id || attemptRef.current?._id || null,
      status: nextStatus,
      updatedAt: nextAttempt.updatedAt || new Date().toISOString(),
      ocrEditedLatex: normalizeOcrLatexForEditor(nextEditedLatexSource),
      ocrEditedSteps: hasField("ocrEditedSteps")
        ? (Array.isArray(nextAttempt.ocrEditedSteps) ? nextAttempt.ocrEditedSteps : [])
        : splitLatexSteps(nextEditedLatexSource),
      ocrLiveLines: hasField("ocrLiveLines")
        ? (Array.isArray(nextAttempt.ocrLiveLines) ? nextAttempt.ocrLiveLines : [])
        : Array.isArray(attemptRef.current?.ocrLiveLines)
          ? attemptRef.current.ocrLiveLines
          : [],
      aiFeedback: hasField("aiFeedback")
        ? String(nextAttempt.aiFeedback || "")
        : String(attemptRef.current?.aiFeedback || ""),
      aiAlignment: hasField("aiAlignment")
        ? (Array.isArray(nextAttempt.aiAlignment) ? nextAttempt.aiAlignment : [])
        : Array.isArray(attemptRef.current?.aiAlignment)
          ? attemptRef.current.aiAlignment
          : [],
      aiFinalCorrect: hasField("aiFinalCorrect")
        ? (typeof nextAttempt.aiFinalCorrect === "boolean" ? nextAttempt.aiFinalCorrect : null)
        : typeof attemptRef.current?.aiFinalCorrect === "boolean"
          ? attemptRef.current.aiFinalCorrect
          : null
    };
  }

  function buildClearedAttemptState(baseAttempt = attemptRef.current) {
    return buildSharedAttemptState({
      _id: baseAttempt?._id || null,
      status: "pending",
      updatedAt: new Date().toISOString(),
      ocrEditedLatex: "",
      ocrEditedSteps: [],
      ocrLiveLines: [],
      aiFeedback: "",
      aiAlignment: [],
      aiFinalCorrect: null
    });
  }

  function publishSharedAttemptState(nextAttempt) {
    if (!roomDocRef.current) return;
    const sharedAttempt = buildSharedAttemptState(nextAttempt);
    if (!sharedAttempt) return;
    setSharedAttemptState(roomDocRef.current, sharedAttempt);
  }

  function sendRoomJsonMessage(payload) {
    const socket = roomSocketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN || !payload || typeof payload !== "object") return false;
    try {
      socket.send(JSON.stringify(payload));
      return true;
    } catch (err) {
      return false;
    }
  }

  async function setRemoteAudioElementStream(stream) {
    const audio = remoteAudioRef.current;
    if (!audio) return;
    if (audio.srcObject !== (stream || null)) {
      audio.srcObject = stream || null;
    }
    await attachVoiceAnalyser(stream || null, "remote");
    if (stream) {
      const playAttempt = audio.play?.();
      if (playAttempt?.catch) {
        playAttempt.catch(() => {});
      }
    }
  }

  function getVoiceTargetUserId(preferredUserId = "") {
    return String(preferredUserId || remoteParticipantIdRef.current || "").trim();
  }

  function updateVoiceParticipantState(userId, nextState) {
    const normalizedUserId = String(userId || "").trim();
    if (!normalizedUserId || normalizedUserId === currentUserIdRef.current) return;
    const joined = Boolean(nextState?.joined);
    const muted = joined ? Boolean(nextState?.muted) : false;
    setVoiceParticipantStates((prev) => {
      const existing = prev?.[normalizedUserId];
      if (existing && existing.joined === joined && existing.muted === muted) {
        return prev;
      }
      const next = {
        ...(prev || {}),
        [normalizedUserId]: {
          joined,
          muted,
          updatedAt: Date.now()
        }
      };
      voiceParticipantStatesRef.current = next;
      return next;
    });
  }

  function requestVoiceStateSnapshot() {
    sendRoomJsonMessage({ type: "voice-state-request" });
  }

  function broadcastVoiceState(nextState) {
    return sendRoomJsonMessage({
      type: "voice-state",
      joined: Boolean(nextState?.joined),
      muted: Boolean(nextState?.joined) ? Boolean(nextState?.muted) : false
    });
  }

  async function ensureVoiceAudioContext() {
    if (voiceAudioContextRef.current) {
      if (voiceAudioContextRef.current.state === "suspended") {
        await voiceAudioContextRef.current.resume().catch(() => {});
      }
      return voiceAudioContextRef.current;
    }
    const AudioContextCtor =
      typeof window === "undefined" ? null : window.AudioContext || window.webkitAudioContext || null;
    if (!AudioContextCtor) {
      return null;
    }
    const context = new AudioContextCtor();
    voiceAudioContextRef.current = context;
    if (context.state === "suspended") {
      await context.resume().catch(() => {});
    }
    return context;
  }

  function clearVoiceAnalyser(ref) {
    const current = ref.current;
    if (!current) return;
    try {
      current.source.disconnect();
    } catch (err) {
      // Ignore disconnect issues during teardown.
    }
    try {
      current.analyser.disconnect();
    } catch (err) {
      // Ignore disconnect issues during teardown.
    }
    ref.current = null;
  }

  function readVoiceLevel(ref) {
    const current = ref.current;
    if (!current?.analyser || !current?.buffer) return 0;
    current.analyser.getByteTimeDomainData(current.buffer);
    let sumSquares = 0;
    for (let index = 0; index < current.buffer.length; index += 1) {
      const centered = (current.buffer[index] - 128) / 128;
      sumSquares += centered * centered;
    }
    const rms = Math.sqrt(sumSquares / current.buffer.length);
    return clampVoiceLevel((rms - 0.01) * 10);
  }

  function stopVoiceMeterLoop(resetLevels = true) {
    if (voiceMeterFrameRef.current) {
      window.cancelAnimationFrame(voiceMeterFrameRef.current);
      voiceMeterFrameRef.current = null;
    }
    voiceMeterCommitRef.current = { at: 0, local: 0, remote: 0 };
    if (resetLevels) {
      setLocalVoiceLevel(0);
      setRemoteVoiceLevel(0);
    }
  }

  function startVoiceMeterLoop() {
    if (voiceMeterFrameRef.current || typeof window === "undefined") return;
    const tick = () => {
      voiceMeterFrameRef.current = window.requestAnimationFrame(tick);
      const localLevel = readVoiceLevel(localVoiceAnalyserRef);
      const remoteLevel = readVoiceLevel(remoteVoiceAnalyserRef);
      const now = performance.now();
      const previous = voiceMeterCommitRef.current;
      const shouldCommit =
        now - previous.at >= 120 ||
        Math.abs(localLevel - previous.local) >= 0.12 ||
        Math.abs(remoteLevel - previous.remote) >= 0.12;
      if (!shouldCommit) return;
      voiceMeterCommitRef.current = { at: now, local: localLevel, remote: remoteLevel };
      setLocalVoiceLevel(localLevel);
      setRemoteVoiceLevel(remoteLevel);
    };
    voiceMeterFrameRef.current = window.requestAnimationFrame(tick);
  }

  async function attachVoiceAnalyser(stream, role) {
    const targetRef = role === "remote" ? remoteVoiceAnalyserRef : localVoiceAnalyserRef;
    clearVoiceAnalyser(targetRef);
    if (!stream?.getAudioTracks?.().length) {
      if (role === "remote") setRemoteVoiceLevel(0);
      else setLocalVoiceLevel(0);
      if (!localVoiceAnalyserRef.current && !remoteVoiceAnalyserRef.current) {
        stopVoiceMeterLoop(false);
      }
      return;
    }
    const context = await ensureVoiceAudioContext();
    if (!context) return;
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.58;
    source.connect(analyser);
    targetRef.current = {
      source,
      analyser,
      buffer: new Uint8Array(analyser.fftSize)
    };
    startVoiceMeterLoop();
  }

  function stopLocalVoiceStream() {
    const stream = voiceLocalStreamRef.current;
    if (!stream) return;
    stream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (err) {
        // Ignore stop failures during teardown.
      }
    });
    voiceLocalStreamRef.current = null;
    clearVoiceAnalyser(localVoiceAnalyserRef);
    setLocalVoiceLevel(0);
    if (!remoteVoiceAnalyserRef.current) {
      stopVoiceMeterLoop(false);
    }
  }

  function closeVoicePeerConnection(options = {}) {
    const peer = voicePeerRef.current;
    voicePeerRef.current = null;
    voiceMakingOfferRef.current = false;
    voiceIgnoreOfferRef.current = false;
    voiceSettingRemoteAnswerPendingRef.current = false;
    voicePendingCandidatesRef.current = [];
    if (peer) {
      peer.ontrack = null;
      peer.onicecandidate = null;
      peer.onnegotiationneeded = null;
      peer.onconnectionstatechange = null;
      peer.oniceconnectionstatechange = null;
      try {
        peer.close();
      } catch (err) {
        // Ignore close failures during teardown.
      }
    }
    const remoteStream = voiceRemoteStreamRef.current;
    voiceRemoteStreamRef.current = null;
    if (options.stopRemoteTracks && remoteStream) {
      remoteStream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (err) {
          // Ignore stop failures during teardown.
        }
      });
    }
    void setRemoteAudioElementStream(null);
  }

  async function ensureVoiceIceServers(forceRefresh = false) {
    if (voiceIceServersRef.current.length && !forceRefresh) {
      return voiceIceServersRef.current;
    }
    const response = await fetch(`${apiBase}/api/collaboration-sessions/${roomToken}/voice-ice`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || "Unable to prepare voice connectivity.");
    }
    const iceServers = Array.isArray(payload?.iceServers) ? payload.iceServers : [];
    if (!iceServers.length) {
      throw new Error("No ICE servers were returned for voice.");
    }
    voiceIceServersRef.current = iceServers;
    return iceServers;
  }

  async function ensureLocalVoiceStream() {
    if (!isSecureVoiceContext) {
      throw new Error("Voice works only on localhost or HTTPS.");
    }
    if (!canUseVoice) {
      throw new Error("This browser does not support live voice in the shared room.");
    }
    const existing = voiceLocalStreamRef.current;
    if (existing?.active && existing.getAudioTracks().length) {
      return existing;
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1
      }
    });
    voiceLocalStreamRef.current = stream;
    await attachVoiceAnalyser(stream, "local");
    return stream;
  }

  function addLocalTracksToVoicePeer(peer, stream) {
    if (!peer || !stream) return;
    stream.getAudioTracks().forEach((track) => {
      const alreadyAdded = peer.getSenders().some((sender) => sender.track && sender.track.id === track.id);
      if (!alreadyAdded) {
        peer.addTrack(track, stream);
      }
    });
  }

  async function createVoicePeerConnection(iceServers) {
    const peer = new RTCPeerConnection({
      iceServers,
      iceCandidatePoolSize: 2
    });
    voicePeerRef.current = peer;
    voicePendingCandidatesRef.current = [];
    voiceIgnoreOfferRef.current = false;

    const remoteStream = new MediaStream();
    voiceRemoteStreamRef.current = remoteStream;
    void setRemoteAudioElementStream(remoteStream);

    if (voiceLocalStreamRef.current) {
      addLocalTracksToVoicePeer(peer, voiceLocalStreamRef.current);
    }

    peer.ontrack = (event) => {
      let stream = voiceRemoteStreamRef.current;
      if (!stream) {
        stream = new MediaStream();
        voiceRemoteStreamRef.current = stream;
      }
      const sourceStream = Array.isArray(event.streams) && event.streams[0] ? event.streams[0] : null;
      if (sourceStream) {
        sourceStream.getAudioTracks().forEach((track) => {
          const exists = stream.getAudioTracks().some((currentTrack) => currentTrack.id === track.id);
          if (!exists) {
            stream.addTrack(track);
          }
        });
      } else if (event.track) {
        const exists = stream.getAudioTracks().some((currentTrack) => currentTrack.id === event.track.id);
        if (!exists) {
          stream.addTrack(event.track);
        }
      }
      void setRemoteAudioElementStream(stream);
      setVoiceError("");
      setVoiceStatus(voiceMutedRef.current ? "Voice connected (muted)" : "Voice connected");
    };

    peer.onicecandidate = (event) => {
      const targetUserId = getVoiceTargetUserId();
      if (!event.candidate || !targetUserId) return;
      sendRoomJsonMessage({
        type: "voice-signal",
        targetUserId,
        candidate: typeof event.candidate.toJSON === "function" ? event.candidate.toJSON() : event.candidate
      });
    };

    peer.onnegotiationneeded = async () => {
      const targetUserId = getVoiceTargetUserId();
      if (!voiceJoinedRef.current || !targetUserId) return;
      try {
        voiceMakingOfferRef.current = true;
        setVoiceError("");
        setVoiceStatus("Connecting voice...");
        await peer.setLocalDescription();
        sendRoomJsonMessage({
          type: "voice-signal",
          targetUserId,
          description: peer.localDescription
            ? {
                type: peer.localDescription.type,
                sdp: peer.localDescription.sdp
              }
            : null
        });
      } catch (err) {
        setVoiceError(err.message || "Voice negotiation failed.");
        setVoiceStatus("Voice setup needs another try.");
      } finally {
        voiceMakingOfferRef.current = false;
      }
    };

    peer.onconnectionstatechange = () => {
      if (peer.connectionState === "connected") {
        setVoiceError("");
        setVoiceStatus(voiceMutedRef.current ? "Voice connected (muted)" : "Voice connected");
        return;
      }
      if (peer.connectionState === "disconnected") {
        setVoiceStatus("Voice reconnecting...");
        return;
      }
      if (peer.connectionState === "failed") {
        closeVoicePeerConnection();
        if (voiceJoinedRef.current) {
          setVoiceStatus("Voice reconnect needed. Leave and join again.");
        }
      }
    };

    peer.oniceconnectionstatechange = () => {
      if (peer.iceConnectionState === "failed") {
        setVoiceStatus("Voice relay failed. Leave and join again.");
      }
    };

    return peer;
  }

  async function ensureVoicePeerReady(preferredUserId = "") {
    const targetUserId = getVoiceTargetUserId(preferredUserId);
    if (!voiceJoinedRef.current || !targetUserId) return null;
    const localStream = await ensureLocalVoiceStream();
    let peer = voicePeerRef.current;
    if (!peer || peer.connectionState === "closed") {
      const iceServers = await ensureVoiceIceServers();
      peer = await createVoicePeerConnection(iceServers);
    }
    addLocalTracksToVoicePeer(peer, localStream);
    return peer;
  }

  async function joinVoiceSession() {
    if (voiceJoinedRef.current) return;
    if (!roomSocketRef.current || roomSocketRef.current.readyState !== WebSocket.OPEN) {
      setVoiceError("Room connection is still starting. Try voice again in a moment.");
      return;
    }
    setVoiceError("");
    setVoiceStatus("Joining voice...");
    try {
      const localStream = await ensureLocalVoiceStream();
      localStream.getAudioTracks().forEach((track) => {
        track.enabled = true;
      });
      voiceJoinedRef.current = true;
      voiceMutedRef.current = false;
      setVoiceJoined(true);
      setVoiceMuted(false);
      broadcastVoiceState({ joined: true, muted: false });
      requestVoiceStateSnapshot();

      const targetUserId = getVoiceTargetUserId();
      const remoteVoiceState = targetUserId ? voiceParticipantStatesRef.current[targetUserId] : null;
      if (targetUserId && remoteVoiceState?.joined) {
        setVoiceStatus("Connecting voice...");
        await ensureVoicePeerReady(targetUserId);
      } else {
        setVoiceStatus(targetUserId ? "Waiting for your partner to join voice" : "Waiting for another student to enter the room");
      }
    } catch (err) {
      voiceJoinedRef.current = false;
      voiceMutedRef.current = false;
      setVoiceJoined(false);
      setVoiceMuted(false);
      closeVoicePeerConnection({ stopRemoteTracks: true });
      stopLocalVoiceStream();
      setVoiceError(err.message || "Unable to join voice.");
      setVoiceStatus("Voice off");
    }
  }

  function toggleVoiceMute() {
    if (!voiceJoinedRef.current) return;
    const stream = voiceLocalStreamRef.current;
    if (!stream) {
      setVoiceError("Voice is not ready yet.");
      return;
    }
    const nextMuted = !voiceMutedRef.current;
    stream.getAudioTracks().forEach((track) => {
      track.enabled = !nextMuted;
    });
    voiceMutedRef.current = nextMuted;
    setVoiceMuted(nextMuted);
    broadcastVoiceState({ joined: true, muted: nextMuted });
    if (nextMuted) {
      setVoiceStatus("Voice connected (muted)");
    } else if (voicePeerRef.current?.connectionState === "connected") {
      setVoiceStatus("Voice connected");
    } else {
      setVoiceStatus("Connecting voice...");
    }
  }

  function leaveVoiceSession(options = {}) {
    const shouldBroadcast = !options.silent && voiceJoinedRef.current;
    if (shouldBroadcast) {
      broadcastVoiceState({ joined: false, muted: false });
      sendRoomJsonMessage({
        type: "voice-signal",
        targetUserId: getVoiceTargetUserId(),
        hangup: true
      });
    }
    closeVoicePeerConnection({ stopRemoteTracks: true });
    stopLocalVoiceStream();
    voiceJoinedRef.current = false;
    voiceMutedRef.current = false;
    setVoiceJoined(false);
    setVoiceMuted(false);
    setVoiceStatus("Voice off");
    if (!options.keepError) {
      setVoiceError("");
    }
  }

  async function handleVoiceSignalPayload(payload) {
    const fromUserId = String(payload?.userId || "").trim();
    if (!fromUserId || fromUserId === currentUserIdRef.current) return;

    if (payload?.hangup) {
      closeVoicePeerConnection({ stopRemoteTracks: true });
      if (voiceJoinedRef.current) {
        setVoiceStatus("Waiting for your partner to join voice");
      }
      return;
    }

    if (!voiceJoinedRef.current) return;

    try {
      const peer = await ensureVoicePeerReady(fromUserId);
      if (!peer) return;

      const description = payload?.description && typeof payload.description === "object" ? payload.description : null;
      const candidate = payload?.candidate && typeof payload.candidate === "object" ? payload.candidate : null;

      if (description) {
        const readyForOffer =
          !voiceMakingOfferRef.current &&
          (peer.signalingState === "stable" || voiceSettingRemoteAnswerPendingRef.current);
        const offerCollision = description.type === "offer" && !readyForOffer;
        voiceIgnoreOfferRef.current = !isVoicePoliteRef.current && offerCollision;
        if (voiceIgnoreOfferRef.current) {
          return;
        }

        voiceSettingRemoteAnswerPendingRef.current = description.type === "answer";
        await peer.setRemoteDescription(description);
        voiceSettingRemoteAnswerPendingRef.current = false;

        if (description.type === "offer") {
          await peer.setLocalDescription();
          sendRoomJsonMessage({
            type: "voice-signal",
            targetUserId: fromUserId,
            description: peer.localDescription
              ? {
                  type: peer.localDescription.type,
                  sdp: peer.localDescription.sdp
                }
              : null
          });
        }

        if (voicePendingCandidatesRef.current.length) {
          const pendingCandidates = voicePendingCandidatesRef.current.splice(0);
          for (const pendingCandidate of pendingCandidates) {
            // Process queued ICE candidates after the remote description lands.
            await peer.addIceCandidate(pendingCandidate);
          }
        }
      }

      if (candidate) {
        const nextCandidate = new RTCIceCandidate(candidate);
        if (peer.remoteDescription) {
          await peer.addIceCandidate(nextCandidate);
        } else {
          voicePendingCandidatesRef.current.push(nextCandidate);
        }
      }
    } catch (err) {
      voiceSettingRemoteAnswerPendingRef.current = false;
      setVoiceError(err.message || "Voice signaling failed.");
      setVoiceStatus("Voice setup needs another try.");
    }
  }

  function handleVoiceStatePayload(payload) {
    const userId = String(payload?.userId || "").trim();
    if (!userId || userId === currentUserIdRef.current) return;
    const joined = Boolean(payload?.joined);
    const muted = joined ? Boolean(payload?.muted) : false;
    updateVoiceParticipantState(userId, { joined, muted });

    if (!joined) {
      if (String(getVoiceTargetUserId()) === userId) {
        closeVoicePeerConnection({ stopRemoteTracks: true });
        if (voiceJoinedRef.current) {
          setVoiceStatus("Waiting for your partner to join voice");
        }
      }
      return;
    }

    if (voiceJoinedRef.current) {
      setVoiceStatus("Connecting voice...");
      void ensureVoicePeerReady(userId);
    }
  }

  function handleVoiceStateRequestPayload(payload) {
    if (String(payload?.userId || "").trim() === currentUserIdRef.current) return;
    if (!voiceJoinedRef.current) return;
    broadcastVoiceState({
      joined: true,
      muted: voiceMutedRef.current
    });
  }

  function syncRemotePreviewState() {
    const values = Object.values(remotePreviewRef.current || {});
    setRemotePreviewLines(values.filter(Boolean));
  }

  function publishPreviewStroke(stroke) {
    const socket = roomSocketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN || !stroke) return;
    try {
      socket.send(
        JSON.stringify({
          type: "preview",
          stroke: {
            strokeId: stroke.strokeId || stroke.id || "",
            color: stroke.color,
            tool: stroke.tool,
            width: stroke.width,
            laneIndex: stroke.laneIndex,
            points: stroke.points
          }
        })
      );
    } catch (err) {
      // Ignore preview send failures.
    }
  }

  function clearPreviewStroke() {
    const socket = roomSocketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    try {
      socket.send(JSON.stringify({ type: "preview-clear" }));
    } catch (err) {
      // Ignore preview send failures.
    }
  }

  function queuePreviewBroadcast() {
    if (previewFrameRef.current || !activeStrokeMetaRef.current) return;
    previewFrameRef.current = window.requestAnimationFrame(() => {
      previewFrameRef.current = null;
      const meta = activeStrokeMetaRef.current;
      const points = activeStrokePointsRef.current.slice();
      if (!meta || points.length < 2) return;
      const previewStroke = {
        ...meta,
        points: points.length >= 4 ? points : [points[0], points[1], points[0] + 0.01, points[1] + 0.01]
      };
      publishPreviewStroke(previewStroke);
    });
  }

  function resetActiveStroke() {
    activeStrokeMetaRef.current = null;
    activeStrokePointsRef.current = [];
    activeStrokePointProcessorRef.current = null;
    activeLaneRef.current = null;
    pointerIdRef.current = null;
  }

  function rebuildLaneInkState(sourceLines = linesRef.current, heights = laneHeightsRef.current) {
    const nextState = {};
    getLaneBounds(heights).forEach((lane) => {
      nextState[getLineIdForLane(lane.index)] = laneHasPenInk(lane.index, sourceLines, heights);
    });
    laneInkStateRef.current = nextState;
  }

  function syncTutorLaneWithInk(sourceLines = linesRef.current, heights = laneHeightsRef.current) {
    const bounds = getLaneBounds(heights);
    const firstEmptyLane = bounds.find((lane) => !laneHasPenInk(lane.index, sourceLines, heights));
    if (firstEmptyLane) {
      setTutorLaneIndex(firstEmptyLane.index);
      return;
    }
    setTutorLaneIndex(bounds.length ? bounds[bounds.length - 1].index : 0);
  }

  function strokesMatch(left, right) {
    const a = normalizeStrokes(left);
    const b = normalizeStrokes(right);
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      const leftStroke = a[i];
      const rightStroke = b[i];
      if (
        String(leftStroke?.strokeId || leftStroke?.id || "") !== String(rightStroke?.strokeId || rightStroke?.id || "") ||
        String(leftStroke?.tool || "") !== String(rightStroke?.tool || "") ||
        String(leftStroke?.color || "") !== String(rightStroke?.color || "") ||
        Number(leftStroke?.laneIndex) !== Number(rightStroke?.laneIndex) ||
        Number(leftStroke?.width) !== Number(rightStroke?.width)
      ) {
        return false;
      }
      const leftPoints = Array.isArray(leftStroke?.points) ? leftStroke.points : [];
      const rightPoints = Array.isArray(rightStroke?.points) ? rightStroke.points : [];
      if (leftPoints.length !== rightPoints.length) return false;
      for (let pointIndex = 0; pointIndex < leftPoints.length; pointIndex += 1) {
        if (Number(leftPoints[pointIndex]) !== Number(rightPoints[pointIndex])) return false;
      }
    }
    return true;
  }

  function applySharedRoomState(options = {}) {
    const doc = roomDocRef.current;
    if (!doc) return;

    const sharedCanvas = readSharedCanvasState(doc);
    const sharedEditorLatex = normalizeOcrLatexForEditor(readSharedEditorLatex(doc) || "");
    const sharedAttempt = readSharedAttemptState(doc);
    const sharedBreakTime = normalizeBreakTimeState(readSharedBreakTimeState(doc) || createInitialBreakTimeState());
    const sharedBreakTimeKey = JSON.stringify(sharedBreakTime);
    const nextAttempt = sharedAttempt
      ? {
          ...sharedAttempt,
          ocrEditedLatex: sharedEditorLatex,
          ocrEditedSteps: splitLatexSteps(sharedEditorLatex)
        }
      : null;

    if (session) {
      setSession((prev) =>
        prev
          ? {
              ...prev,
              canvasState: {
                ...(prev.canvasState || {}),
                revision: Number(sharedCanvas.revision || 0),
                stageWidth: Number(sharedCanvas.stageWidth || prev.canvasState?.stageWidth || 1200),
                stageHeight: Number(sharedCanvas.stageHeight || prev.canvasState?.stageHeight || 760),
                laneHeights: sanitizeLaneHeights(sharedCanvas.laneHeights),
                updatedAt: sharedCanvas.updatedAt || prev.canvasState?.updatedAt || null
              }
            }
          : prev
      );
    }

    if (sharedBreakTimeKey !== breakTimeStateKeyRef.current) {
      breakTimeStateKeyRef.current = sharedBreakTimeKey;
      setBreakTimeState(sharedBreakTime);
    }

    if (nextAttempt) {
      applyAttemptState(nextAttempt, {
        partial: true,
        replaceEditor: false,
        preserveAnalysis: false
      });
    }
    if (sharedEditorLatex !== normalizeOcrLatexForEditor(ocrEditedLatexRef.current || "")) {
      updateOcrEditor(sharedEditorLatex, { fromShared: true });
    }

    const shouldAdoptCanvas = options.forceCanvas || !drawingRef.current;
    if (!shouldAdoptCanvas) return;

    const nextLines = normalizeStrokes(sharedCanvas.strokes);
    const nextHeights = sanitizeLaneHeights(sharedCanvas.laneHeights);
    const strokesChanged = !strokesMatch(nextLines, linesRef.current);
    if (strokesChanged) {
      linesRef.current = nextLines;
      setLines(nextLines);
      syncInkClusters();
    }
    if (sharedCanvas.paperStyle && sharedCanvas.paperStyle !== paperStyleRef.current) {
      paperStyleRef.current = sharedCanvas.paperStyle;
      setPaperStyle(sharedCanvas.paperStyle);
    }
    const nextLaneKey = serializeLaneHeights(nextHeights);
    if (nextLaneKey !== serializeLaneHeights(laneHeightsRef.current)) {
      laneHeightsRef.current = nextHeights;
      setLaneHeights(nextHeights);
      lastLaneSyncKeyRef.current = nextLaneKey;
    }
    lastRevisionRef.current = Number(sharedCanvas.revision || lastRevisionRef.current || 0);
    rebuildLaneInkState(nextLines, nextHeights);
    syncTutorLaneWithInk(nextLines, nextHeights);
  }

  function connectRoomSocket() {
    if (!roomToken || !token) return;
    roomSocketEnabledRef.current = true;
    const existingSocket = roomSocketRef.current;
    if (existingSocket && (existingSocket.readyState === WebSocket.OPEN || existingSocket.readyState === WebSocket.CONNECTING)) {
      return;
    }
    if (roomReconnectTimerRef.current) {
      window.clearTimeout(roomReconnectTimerRef.current);
      roomReconnectTimerRef.current = null;
    }

    let doc = roomDocRef.current;
    if (!doc) {
      doc = createCollaborationDoc();
      roomDocRef.current = doc;
    }
    if (!roomDocObserverRef.current) {
      const handleDocUpdate = () => {
        applySharedRoomState();
      };
      roomDocObserverRef.current = handleDocUpdate;
      doc.on("update", handleDocUpdate);
    }
    if (!roomDocSenderRef.current) {
      const handleDocSend = (update, origin) => {
        if (origin === "remote") return;
        const activeSocket = roomSocketRef.current;
        if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN) return;
        try {
          activeSocket.send(update);
        } catch (err) {
          // Ignore socket send failures; reconnect flow handles recovery.
        }
      };
      roomDocSenderRef.current = handleDocSend;
      doc.on("update", handleDocSend);
    }

    const socket = new WebSocket(buildCollaborationWsUrl(apiBase, roomToken, token));
    roomSocketRef.current = socket;

    socket.binaryType = "arraybuffer";
    socket.onopen = () => {
      if (roomSocketRef.current !== socket) return;
      roomSocketConnectedRef.current = true;
      roomReconnectAttemptRef.current = 0;
      setRoomSocketConnected(true);
      setNotice("");
      try {
        socket.send(Y.encodeStateAsUpdate(doc));
      } catch (err) {
        setNotice("Live room failed to replay pending changes.");
      }
      requestVoiceStateSnapshot();
      if (voiceJoinedRef.current) {
        broadcastVoiceState({
          joined: true,
          muted: voiceMutedRef.current
        });
      }
    };
    socket.onmessage = (event) => {
      if (typeof event.data === "string") {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.type === "participants" && Array.isArray(payload.participants)) {
            setSession((prev) =>
              prev
                ? {
                    ...prev,
                    participants: payload.participants
                  }
                : prev
            );
            return;
          }
          if (payload?.type === "preview" && payload?.stroke && payload?.userId) {
            remotePreviewRef.current[payload.userId] = payload.stroke;
            syncRemotePreviewState();
            return;
          }
          if (payload?.type === "preview-clear" && payload?.userId) {
            delete remotePreviewRef.current[payload.userId];
            syncRemotePreviewState();
            return;
          }
          if (payload?.type === "voice-state" && payload?.userId) {
            handleVoiceStatePayload(payload);
            return;
          }
          if (payload?.type === "voice-state-request") {
            handleVoiceStateRequestPayload(payload);
            return;
          }
          if (payload?.type === "voice-signal" && payload?.userId) {
            void handleVoiceSignalPayload(payload);
          }
        } catch (err) {
          // Ignore malformed preview messages.
        }
        return;
      }
      try {
        const update =
          event.data instanceof ArrayBuffer ? new Uint8Array(event.data) : new Uint8Array(event.data);
        Y.applyUpdate(doc, update, "remote");
      } catch (err) {
        setNotice("Live room sync failed to apply an update.");
      }
    };
    socket.onerror = () => {
      if (roomSocketRef.current !== socket) return;
      roomSocketConnectedRef.current = false;
      setRoomSocketConnected(false);
      setNotice("Live room connection interrupted.");
      if (voiceJoinedRef.current) {
        setVoiceStatus("Room reconnecting. Voice may pause briefly.");
      }
    };
    socket.onclose = () => {
      if (roomSocketRef.current !== socket) return;
      roomSocketConnectedRef.current = false;
      roomSocketRef.current = null;
      setRoomSocketConnected(false);
      Object.keys(remotePreviewRef.current).forEach((key) => delete remotePreviewRef.current[key]);
      syncRemotePreviewState();
      if (voiceJoinedRef.current) {
        setVoiceStatus("Room reconnecting. Voice may pause briefly.");
      }
      if (!roomSocketEnabledRef.current) return;
      const nextAttempt = Math.min(roomReconnectAttemptRef.current + 1, 8);
      roomReconnectAttemptRef.current = nextAttempt;
      const delayMs = Math.min(1200 * nextAttempt, 5000);
      roomReconnectTimerRef.current = window.setTimeout(() => {
        roomReconnectTimerRef.current = null;
        connectRoomSocket();
      }, delayMs);
    };
  }

  function disconnectRoomSocket() {
    roomSocketEnabledRef.current = false;
    if (roomReconnectTimerRef.current) {
      window.clearTimeout(roomReconnectTimerRef.current);
      roomReconnectTimerRef.current = null;
    }
    if (roomPollTimerRef.current) {
      window.clearTimeout(roomPollTimerRef.current);
      roomPollTimerRef.current = null;
    }
    roomPollBusyRef.current = false;
    roomReconnectAttemptRef.current = 0;
    if (previewFrameRef.current) {
      window.cancelAnimationFrame(previewFrameRef.current);
      previewFrameRef.current = null;
    }
    const socket = roomSocketRef.current;
    roomSocketRef.current = null;
    if (socket) {
      try {
        socket.close();
      } catch (err) {
        // Ignore close failures.
      }
    }
    const doc = roomDocRef.current;
    if (doc) {
      if (roomDocObserverRef.current) {
        doc.off("update", roomDocObserverRef.current);
      }
      if (roomDocSenderRef.current) {
        doc.off("update", roomDocSenderRef.current);
      }
      doc.destroy();
    }
    roomDocRef.current = null;
    roomDocObserverRef.current = null;
    roomDocSenderRef.current = null;
    Object.keys(remotePreviewRef.current).forEach((key) => delete remotePreviewRef.current[key]);
    setRemotePreviewLines([]);
  }

  async function pollRoomState() {
    if (!token || !roomToken || roomPollBusyRef.current) return;
    roomPollBusyRef.current = true;
    try {
      const params = new URLSearchParams({
        delta: "1",
        sinceRevision: String(Math.max(0, Number(lastRevisionRef.current || 0))),
        attemptUpdatedAt: String(Math.max(0, Number(lastAttemptSyncAtRef.current || 0)))
      });
      const response = await fetch(`${apiBase}/api/collaboration-sessions/${roomToken}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.status === 204) return;
      if (!response.ok) return;
      const payload = await response.json().catch(() => null);
      if (!payload) return;
      applySessionPayload(payload, {
        replaceEditor: false,
        preserveAnalysis: false
      });
    } catch (err) {
      // Ignore polling failures while websocket reconnect is handling recovery.
    } finally {
      roomPollBusyRef.current = false;
    }
  }

  async function persistCanvasBackup(endpoint, body) {
    if (!token || !roomToken) return;
    try {
      await fetch(`${apiBase}/api/collaboration-sessions/${roomToken}${endpoint}`, {
        method: endpoint === "/strokes" ? "POST" : "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body || {})
      });
    } catch (err) {
      // Keep local slate interactions responsive even if the durability backup fails.
    }
  }

  function getAttemptUpdatedAtMs(value) {
    const time = value?.updatedAt ? new Date(value.updatedAt).getTime() : 0;
    return Number.isFinite(time) ? time : 0;
  }

  function mergeAttemptPatch(currentAttempt, nextAttempt) {
    return {
      ...(currentAttempt || {}),
      ...(nextAttempt || {})
    };
  }

  function applyCanvasSnapshot(canvasState, nextAttempt, options = {}) {
    if (!canvasState) return;
    const revision = Number(canvasState?.revision || 0);
    if (options.skipCanvasAdoption) {
      lastRevisionRef.current = revision;
      lastLaneSyncKeyRef.current = serializeLaneHeights(canvasState?.laneHeights);
      return;
    }
    if (!options.forceCanvas && !canAdoptRemoteCanvas()) return;
    if (!options.forceCanvas && lastRevisionRef.current === revision) return;

    const nextLines = normalizeStrokes(canvasState?.strokes);
    const nextHeights = sanitizeLaneHeights(canvasState?.laneHeights);

    linesRef.current = nextLines;
    laneHeightsRef.current = nextHeights;
    setLines(nextLines);
    setLaneHeights(nextHeights);
    lastRevisionRef.current = revision;
    laneLocalChangeRef.current = false;
    lastLaneSyncKeyRef.current = serializeLaneHeights(nextHeights);
    rebuildLaneInkState(nextLines, nextHeights);
    syncTutorLaneWithInk(nextLines, nextHeights);
    if (nextAttempt) {
      setAnalysisLaneMap(
        buildMathLaneMapFromAttempt(nextAttempt, nextLines, (laneIndex) => laneHasPenInk(laneIndex, nextLines, nextHeights))
      );
    }
  }

  function mergeStrokesById(sourceLines, incomingLines) {
    const next = [...(Array.isArray(sourceLines) ? sourceLines : [])];
    const seen = new Set(next.map((stroke) => String(stroke?.strokeId || stroke?.id || "")));
    normalizeStrokes(incomingLines).forEach((stroke) => {
      const strokeId = String(stroke?.strokeId || stroke?.id || "");
      if (!strokeId || seen.has(strokeId)) return;
      seen.add(strokeId);
      next.push(stroke);
    });
    return next;
  }

  function applyCanvasDelta(delta, nextAttempt, options = {}) {
    if (!delta) return;
    const operations = Array.isArray(delta?.operations) ? delta.operations : [];
    const toRevision = Number(delta?.toRevision || 0);
    if (options.skipCanvasAdoption) {
      lastRevisionRef.current = toRevision;
      return;
    }
    if (!operations.length) {
      lastRevisionRef.current = toRevision;
      return;
    }
    if (!options.forceCanvas && !canAdoptRemoteCanvas()) return;

    let nextLines = linesRef.current.slice();
    let nextHeights = laneHeightsRef.current.slice();

    operations.forEach((operation) => {
      const opType = String(operation?.type || "");
      if (opType === "appendStrokes") {
        nextLines = mergeStrokesById(nextLines, operation?.strokes);
        return;
      }
      if (opType === "removeStroke") {
        const strokeId = String(operation?.strokeId || "").trim();
        if (!strokeId) return;
        nextLines = nextLines.filter((stroke) => String(stroke?.strokeId || stroke?.id || "") !== strokeId);
        return;
      }
      if (opType === "laneHeights") {
        nextHeights = sanitizeLaneHeights(operation?.laneHeights);
        return;
      }
      if (opType === "clear") {
        nextLines = [];
        nextHeights = sanitizeLaneHeights(operation?.laneHeights);
      }
    });

    linesRef.current = nextLines;
    laneHeightsRef.current = nextHeights;
    setLines(nextLines);
    setLaneHeights(nextHeights);
    lastRevisionRef.current = toRevision;
    laneLocalChangeRef.current = false;
    lastLaneSyncKeyRef.current = serializeLaneHeights(nextHeights);
    rebuildLaneInkState(nextLines, nextHeights);
    syncTutorLaneWithInk(nextLines, nextHeights);
    if (nextAttempt) {
      setAnalysisLaneMap(
        buildMathLaneMapFromAttempt(nextAttempt, nextLines, (laneIndex) => laneHasPenInk(laneIndex, nextLines, nextHeights))
      );
    }
  }

  function applyAnalysisStateFromAttempt(nextAttempt, options = {}) {
    if (options.preserveAnalysis) return;
    const rawFeedback = String(nextAttempt?.aiFeedback || "");
    if (!rawFeedback.trim()) {
      setAnalysisStatus("");
      setAnalysisFeedback("");
      setAnalysisData(null);
      setAnalysisAlignment([]);
      setAnalysisFinalCorrect(null);
      setSelectedDiagStep(null);
      return;
    }

    const parsed = parseAnalysisFeedback(rawFeedback);
    setAnalysisStatus("Analysis complete");
    if (parsed && parsed.analysis) {
      const alignment = Array.isArray(parsed.alignment) ? parsed.alignment : [];
      const firstIssue = alignment.find(
        (item) => item?.status && item.status !== "correct" && Number.isFinite(Number(item.student_step))
      );
      setAnalysisData(parsed.analysis);
      setAnalysisAlignment(alignment);
      setAnalysisFinalCorrect(typeof parsed.final_answer_correct === "boolean" ? parsed.final_answer_correct : null);
      setSelectedDiagStep(firstIssue ? Number(firstIssue.student_step) : null);
      setAnalysisFeedback("");
      return;
    }

    setAnalysisData(null);
    setAnalysisAlignment([]);
    setAnalysisFinalCorrect(null);
    setSelectedDiagStep(null);
    setAnalysisFeedback(rawFeedback || "No feedback");
  }

  function applyAttemptState(nextAttempt, options = {}) {
    if (!nextAttempt) return;
    const mergedAttempt = options.partial ? mergeAttemptPatch(attemptRef.current, nextAttempt) : nextAttempt;
    const mergedUpdatedAtMs = getAttemptUpdatedAtMs(mergedAttempt);
    if (mergedUpdatedAtMs > 0) {
      lastAttemptSyncAtRef.current = Math.max(lastAttemptSyncAtRef.current, mergedUpdatedAtMs);
    }
    attemptRef.current = mergedAttempt;
    setAttempt(mergedAttempt);
    const rows = normalizeOcrLaneRowsFromAttempt(mergedAttempt);
    const serverLatex = normalizeOcrLatexForEditor(mergedAttempt?.ocrEditedLatex || "");
    lastServerOcrLatexRef.current = serverLatex;
    const shouldReplaceEditor =
      options.replaceEditor ||
      !ocrEditorDirtyRef.current ||
      (!serverLatex.trim() && !rows.length);
    const shouldResetSharedSlate =
      !serverLatex.trim() &&
      rows.length === 0 &&
      !(Array.isArray(mergedAttempt?.ocrLiveLines) ? mergedAttempt.ocrLiveLines.length : 0) &&
      !String(mergedAttempt?.aiFeedback || "").trim();

    setOcrLaneRows(rows);
    if (shouldReplaceEditor) {
      updateOcrEditor(serverLatex, { fromServer: true });
    }
    if (shouldResetSharedSlate) {
      clearTutorHint();
      setTutorHintHistory([]);
      setTutorChatOpen(false);
    }

    setAnalysisLaneMap(
      buildMathLaneMapFromAttempt(mergedAttempt, linesRef.current, (laneIndex) => laneHasPenInk(laneIndex))
    );
    applyAnalysisStateFromAttempt(mergedAttempt, options);
  }
  applyAttemptStateRef.current = applyAttemptState;

  function canAdoptRemoteCanvas() {
    return !drawingRef.current && !laneDragRef.current && pendingCanvasOpsRef.current === 0;
  }

  function applySessionPayload(payload, options = {}) {
    const nextSession = payload?.session || null;
    const nextAttempt = payload?.attempt || payload?.attemptSync || null;

    if (nextSession) setSession(nextSession);
    if (payload?.frame !== undefined) setFrame(payload.frame || null);
    else if (nextAttempt?.frameId) setFrame(nextAttempt.frameId);
    if (payload?.question !== undefined) setQuestion(payload.question || null);
    else if (nextAttempt?.questionId) setQuestion(nextAttempt.questionId);
    if (nextAttempt) {
      applyAttemptState(nextAttempt, {
        ...options,
        partial: Boolean(payload?.attemptSync) && !payload?.attempt
      });
    }

    if (!nextSession) return;
    if (payload?.canvasDelta) {
      applyCanvasDelta(payload.canvasDelta, nextAttempt || attemptRef.current, options);
      return;
    }
    if (payload?.fullCanvas) {
      applyCanvasSnapshot(payload.fullCanvas, nextAttempt || attemptRef.current, options);
      return;
    }
    if (Array.isArray(nextSession?.canvasState?.strokes)) {
      applyCanvasSnapshot(nextSession.canvasState, nextAttempt || attemptRef.current, options);
      return;
    }
    if (options.skipCanvasAdoption) {
      lastRevisionRef.current = Number(nextSession?.canvasState?.revision || lastRevisionRef.current || 0);
      lastLaneSyncKeyRef.current = serializeLaneHeights(nextSession?.canvasState?.laneHeights);
    }
  }

  async function loadSession(options = {}) {
    if (!token || !roomToken) return;
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${apiBase}/api/collaboration-sessions/${roomToken}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Unable to load collaboration room");
      }

      const payload = await response.json();
      applySessionPayload(payload, {
        forceCanvas: true,
        replaceEditor: true,
        preserveAnalysis: false
      });
      if (payload?.session?.currentParticipant?.color) {
        setStrokeColor(String(payload.session.currentParticipant.color || "#1d4ed8"));
      }
      connectRoomSocket();
      setNotice("");
    } catch (err) {
      setError(err.message || "Unable to load collaboration room");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!tokenRef.current || !roomToken) return undefined;
    void loadSession({ replaceEditor: true });
    return () => {
      leaveVoiceSession({ keepError: true });
      disconnectRoomSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, roomToken, hasToken]);

  useEffect(() => {
    if (!tokenRef.current || !roomToken || roomSocketConnected) return undefined;
    let cancelled = false;

    const scheduleNextPoll = (delayMs = 1000) => {
      if (roomPollTimerRef.current) {
        window.clearTimeout(roomPollTimerRef.current);
      }
      roomPollTimerRef.current = window.setTimeout(async () => {
        roomPollTimerRef.current = null;
        if (cancelled || roomSocketConnectedRef.current) return;
        await pollRoomState();
        if (!cancelled && !roomSocketConnectedRef.current) {
          scheduleNextPoll(1400);
        }
      }, delayMs);
    };

    scheduleNextPoll(900);
    return () => {
      cancelled = true;
      if (roomPollTimerRef.current) {
        window.clearTimeout(roomPollTimerRef.current);
        roomPollTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, roomSocketConnected, roomToken, hasToken]);

  useEffect(() => {
    if (!currentAttemptId || !ocrEditorDirtyRef.current) return undefined;
    if (editorSyncTimerRef.current) clearTimeout(editorSyncTimerRef.current);
    editorSyncTimerRef.current = setTimeout(async () => {
      editorSyncTimerRef.current = null;
      const nextLatex = normalizeOcrLatexForEditor(ocrEditedLatexRef.current || "");
      if (!nextLatex && !lastServerOcrLatexRef.current) {
        ocrEditorDirtyRef.current = false;
        return;
      }

      try {
        const response = await fetch(`${apiBase}/api/attempts/${currentAttemptId}/ocr`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            editedLatex: nextLatex,
            editedSteps: splitLatexSteps(nextLatex)
          })
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || "Failed to sync Math Editor");
        }
        const payload = await response.json().catch(() => ({}));
        if (payload?.attempt) {
          applyAttemptStateRef.current?.(payload.attempt, { replaceEditor: true, preserveAnalysis: true });
          publishSharedAttemptState(payload.attempt);
        } else {
          updateOcrEditor(nextLatex, { fromServer: true });
        }
      } catch (err) {
        setNotice(err.message || "Failed to sync Math Editor");
      }
    }, 320);

    return () => {
      if (editorSyncTimerRef.current) {
        clearTimeout(editorSyncTimerRef.current);
        editorSyncTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, currentAttemptId, ocrEditedLatex, token]);

  async function copyInviteLink() {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard?.writeText(inviteUrl);
      setNotice("Room link copied.");
    } catch (err) {
      setNotice("Copy the room link manually.");
    }
  }

  function setOcrFrozen(nextFrozen) {
    ocrFrozenRef.current = Boolean(nextFrozen);
    if (ocrFrozenRef.current && ocrTimerRef.current) {
      clearTimeout(ocrTimerRef.current);
      ocrTimerRef.current = null;
    }
  }

  function queueLiveLineOcr(delay = OCR_DELAY_MS) {
    if (ocrFrozenRef.current) return;
    if (ocrTimerRef.current) clearTimeout(ocrTimerRef.current);
    ocrTimerRef.current = setTimeout(() => {
      ocrTimerRef.current = null;
      void flushLiveLineOcr();
    }, Math.max(OCR_MIN_DELAY_MS, Number(delay) || OCR_DELAY_MS));
  }

  function getLaneStrokes(laneIndex, sourceLines = linesRef.current) {
    return (Array.isArray(sourceLines) ? sourceLines : []).filter(
      (stroke) => Number(stroke?.laneIndex) === Number(laneIndex)
    );
  }

  function getLaneInkExtents(laneIndex, sourceLines = linesRef.current, heights = laneHeightsRef.current) {
    const bounds = getLaneBounds(heights);
    const lane = bounds.find((item) => Number(item.index) === Number(laneIndex));
    if (!lane) return null;

    const strokes = getLaneStrokes(laneIndex, sourceLines);
    if (!strokes.length) return null;

    const laneHeight = Math.max(36, Math.ceil(lane.bottom - lane.top));
    const laneWidth = Math.max(40, Math.ceil(stageMetricsRef.current.width || 360));
    if (!renderCanvasRef.current) renderCanvasRef.current = document.createElement("canvas");
    const canvas = renderCanvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    canvas.width = laneWidth;
    canvas.height = laneHeight;
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, laneWidth, laneHeight);
    ctx.strokeStyle = "#000000";
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    strokes.forEach((stroke) => {
      const points = Array.isArray(stroke?.points) ? stroke.points : [];
      if (points.length < 4) return;
      ctx.globalCompositeOperation = stroke?.tool === "eraser" ? "destination-out" : "source-over";
      ctx.lineWidth = Number(stroke?.width) || (stroke?.tool === "eraser" ? 28 : 2.5);
      ctx.beginPath();
      ctx.moveTo(points[0], points[1] - lane.top);
      for (let i = 2; i < points.length; i += 2) {
        ctx.lineTo(points[i], points[i + 1] - lane.top);
      }
      ctx.stroke();
    });

    const image = ctx.getImageData(0, 0, laneWidth, laneHeight);
    const px = image.data;
    let hasInk = false;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    for (let y = 0; y < laneHeight; y += 1) {
      for (let x = 0; x < laneWidth; x += 1) {
        const idx = (y * laneWidth + x) * 4;
        const r = px[idx];
        const g = px[idx + 1];
        const b = px[idx + 2];
        const a = px[idx + 3];
        if (a > 0 && (r < 245 || g < 245 || b < 245)) {
          hasInk = true;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (!hasInk) return null;
    return {
      laneTop: lane.top,
      laneBottom: lane.bottom,
      minY,
      maxY
    };
  }

  function renderLaneImageData(laneIndex, sourceLines = linesRef.current, heights = laneHeightsRef.current) {
    const bounds = getLaneBounds(heights);
    const lane = bounds.find((item) => Number(item.index) === Number(laneIndex));
    if (!lane) return { clearLine: true, dataUrl: "" };

    const strokes = getLaneStrokes(laneIndex, sourceLines);
    if (!strokes.length) return { clearLine: true, dataUrl: "" };

    const laneHeight = Math.max(36, Math.ceil(lane.bottom - lane.top));
    const laneWidth = Math.max(40, Math.ceil(stageMetricsRef.current.width || 360));
    if (!renderCanvasRef.current) renderCanvasRef.current = document.createElement("canvas");
    const laneCanvas = renderCanvasRef.current;
    const laneCtx = laneCanvas.getContext("2d");
    if (!laneCtx) return { clearLine: true, dataUrl: "" };

    laneCanvas.width = laneWidth;
    laneCanvas.height = laneHeight;
    laneCtx.globalCompositeOperation = "source-over";
    laneCtx.fillStyle = "#ffffff";
    laneCtx.fillRect(0, 0, laneWidth, laneHeight);
    laneCtx.strokeStyle = "#000000";
    laneCtx.lineCap = "round";
    laneCtx.lineJoin = "round";

    strokes.forEach((stroke) => {
      const points = Array.isArray(stroke?.points) ? stroke.points : [];
      if (points.length < 4) return;
      laneCtx.globalCompositeOperation = stroke?.tool === "eraser" ? "destination-out" : "source-over";
      laneCtx.lineWidth = Number(stroke?.width) || (stroke?.tool === "eraser" ? 28 : 2.5);
      laneCtx.beginPath();
      laneCtx.moveTo(points[0], points[1] - lane.top);
      for (let i = 2; i < points.length; i += 2) {
        laneCtx.lineTo(points[i], points[i + 1] - lane.top);
      }
      laneCtx.stroke();
    });

    const image = laneCtx.getImageData(0, 0, laneWidth, laneHeight);
    const px = image.data;
    let hasInk = false;
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    for (let y = 0; y < laneHeight; y += 1) {
      for (let x = 0; x < laneWidth; x += 1) {
        const idx = (y * laneWidth + x) * 4;
        const r = px[idx];
        const g = px[idx + 1];
        const b = px[idx + 2];
        const a = px[idx + 3];
        if (a > 0 && (r < 245 || g < 245 || b < 245)) {
          hasInk = true;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (!hasInk) return { clearLine: true, dataUrl: "" };

    minX = Math.max(0, minX - LANE_PAD);
    minY = Math.max(0, minY - LANE_PAD);
    maxX = Math.min(laneWidth - 1, maxX + LANE_PAD);
    maxY = Math.min(laneHeight - 1, maxY + LANE_PAD);

    const width = Math.max(40, Math.ceil(maxX - minX + 1));
    const height = Math.max(36, Math.ceil(maxY - minY + 1));
    if (!cropCanvasRef.current) cropCanvasRef.current = document.createElement("canvas");
    const cropCanvas = cropCanvasRef.current;
    const cropCtx = cropCanvas.getContext("2d");
    if (!cropCtx) return { clearLine: true, dataUrl: "" };

    cropCanvas.width = width;
    cropCanvas.height = height;
    cropCtx.globalCompositeOperation = "source-over";
    cropCtx.fillStyle = "#ffffff";
    cropCtx.fillRect(0, 0, width, height);
    cropCtx.drawImage(laneCanvas, minX, minY, width, height, 0, 0, width, height);
    return { clearLine: false, dataUrl: cropCanvas.toDataURL("image/png") };
  }

  function laneHasPenInk(laneIndex, sourceLines = linesRef.current, heights = laneHeightsRef.current) {
    return !renderLaneImageData(laneIndex, sourceLines, heights).clearLine;
  }

  function enqueueLiveLineUpdate(update) {
    const lineId = String(update?.lineId || "").trim();
    if (!lineId) return;

    const nextOp = {
      lineId,
      lineOrder: Number.isFinite(Number(update?.lineOrder)) ? Number(update.lineOrder) : 0,
      revision: Number.isFinite(Number(update?.revision)) ? Number(update.revision) : 0,
      clearLine: Boolean(update?.clearLine),
      dataUrl: update?.clearLine ? "" : String(update?.dataUrl || "")
    };

    const current = pendingLinesRef.current.get(lineId);
    if (current && Number(current.revision || 0) > nextOp.revision) return;
    pendingLinesRef.current.set(lineId, nextOp);
    if (!pendingOrderRef.current.includes(lineId)) {
      pendingOrderRef.current.push(lineId);
    }
    queueLiveLineOcr(update?.delay);
  }

  function enqueueLaneOcr(laneIndex, options = {}) {
    const idx = Number(laneIndex);
    if (!Number.isFinite(idx) || idx < 0) return;
    if (ocrFrozenRef.current && !options.allowWhileFrozen) return;

    const lineId = getLineIdForLane(idx);
    const currentRevision = Number(laneRevisionRef.current[lineId] || 0);
    const nextRevision = options.bumpRevision ? currentRevision + 1 : currentRevision;
    laneRevisionRef.current[lineId] = nextRevision;
    const rendered = renderLaneImageData(idx);
    const ackRevision = Number(laneAckRevisionRef.current[lineId] || 0);
    if (!options.force && !options.bumpRevision && nextRevision <= ackRevision) return;

    enqueueLiveLineUpdate({
      lineId,
      lineOrder: idx,
      revision: nextRevision,
      clearLine: rendered.clearLine,
      dataUrl: rendered.dataUrl,
      delay: options.delay
    });
  }

  function markLaneDirty(laneIndex) {
    const idx = Number(laneIndex);
    if (!Number.isFinite(idx) || idx < 0) return;
    const lineId = getLineIdForLane(idx);
    laneRevisionRef.current[lineId] = Number(laneRevisionRef.current[lineId] || 0) + 1;
  }

  function enqueueDirtyLanes(maxLaneIndex, options = {}) {
    if (ocrFrozenRef.current && !options.allowWhileFrozen) return;
    const maxIdx = Number.isFinite(Number(maxLaneIndex)) ? Number(maxLaneIndex) : Number.MAX_SAFE_INTEGER;
    getLaneBounds(laneHeightsRef.current).forEach((lane) => {
      const idx = Number(lane.index);
      if (!Number.isFinite(idx) || idx < 0 || idx > maxIdx) return;
      const lineId = getLineIdForLane(idx);
      const currentRevision = Number(laneRevisionRef.current[lineId] || 0);
      const ackRevision = Number(laneAckRevisionRef.current[lineId] || 0);
      if (currentRevision > ackRevision) {
        enqueueLaneOcr(idx, {
          force: true,
          bumpRevision: false,
          delay: options.delay ?? OCR_MIN_DELAY_MS
        });
      }
    });
  }

  function appendBlankSlateLanes(count = LANE_BATCH) {
    const laneCount = Math.max(1, Math.min(24, Math.floor(Number(count) || LANE_BATCH)));
    laneLocalChangeRef.current = true;
    setLaneHeights((prev) => {
      const next = [...prev, ...Array.from({ length: laneCount }, () => DEFAULT_LANE_HEIGHT)];
      if (roomDocRef.current) {
        replaceLaneHeights(roomDocRef.current, next);
      }
      return next;
    });
  }

  function syncInkClusters() {
    const canvasWidth = Math.max(MIN_STAGE_WIDTH, Number(stageCanvasWidth) || 800);
    const prevClusters = stepClustersRef.current || [];
    const clusters = updateStepClustersIncremental(prevClusters, linesRef.current, { canvasWidth });
    setStepClusters(clusters);
    stepClustersRef.current = clusters;
    return clusters;
  }

  function getClusterFingerprint(cluster) {
    if (!cluster || !Array.isArray(cluster.strokes)) return "";
    const strokeIds = cluster.strokes.map((s) => s.id || s.strokeId || "").sort().join(",");
    const b = cluster.bounds || {};
    return `${strokeIds}:${Math.round(b.minX || 0)},${Math.round(b.minY || 0)},${Math.round(b.maxX || 0)},${Math.round(b.maxY || 0)}`;
  }

  function enqueueClusterOcr(cluster, options = {}) {
    if (!cluster || !cluster.id) return;
    if (ocrFrozenRef.current && !options.allowWhileFrozen) return;

    const fingerprint = getClusterFingerprint(cluster);
    const lastSent = clusterFingerprintsRef.current[cluster.id];

    // Avoid redundant OCR calls if ink in this step hasn't changed
    if (!options.force && lastSent === fingerprint) {
      return;
    }
    clusterFingerprintsRef.current[cluster.id] = fingerprint;

    const rendered = renderClusterImageData(cluster, {
      canvas: cropCanvasRef.current,
      padding: 28
    });

    const ackRev = Number(laneAckRevisionRef.current[cluster.id] || 0);
    const currentRev = Number(laneRevisionRef.current[cluster.id] || 0);
    const baseRev = Number(cluster.revision || 1);
    const nextRevision = Math.max(ackRev + 1, currentRev + 1, baseRev);
    laneRevisionRef.current[cluster.id] = nextRevision;

    enqueueLiveLineUpdate({
      lineId: cluster.id,
      lineOrder: Number.isFinite(Number(cluster.stepNo)) ? Math.max(0, Number(cluster.stepNo) - 1) : 0,
      revision: nextRevision,
      clearLine: rendered.clearLine,
      dataUrl: rendered.dataUrl,
      delay: options.delay ?? 50,
      allowWhileFrozen: options.allowWhileFrozen
    });
  }

  function onSlateStrokeFinished() {
    setSelectedDiagStep(null);
    const clusters = syncInkClusters();
    setTutorLaneIndex(clusters.length);
    const liveOcrFrozen = ocrFrozenRef.current;
    if (liveOcrFrozen || !clusters.length) return;

    const currentCount = clusters.length;
    const previousCount = previousMathClustersCountRef.current;
    previousMathClustersCountRef.current = currentCount;

    // Kinematic step break: new step line started!
    // Commit previous completed step line immediately to OCR.
    if (currentCount > 1 && currentCount > previousCount) {
      const finishedCluster = clusters[currentCount - 2];
      if (finishedCluster) {
        if (clusterDebounceTimersRef.current.has(finishedCluster.id)) {
          clearTimeout(clusterDebounceTimersRef.current.get(finishedCluster.id));
          clusterDebounceTimersRef.current.delete(finishedCluster.id);
        }
        enqueueClusterOcr(finishedCluster, { delay: 30 });
      }
    }

    // Debounce all clusters with changed ink (including edits/erasures in earlier steps)
    const changedClusters = clusters.filter(
      (c) => getClusterFingerprint(c) !== clusterFingerprintsRef.current[c.id]
    );

    for (const cluster of changedClusters) {
      if (clusterDebounceTimersRef.current.has(cluster.id)) {
        clearTimeout(clusterDebounceTimersRef.current.get(cluster.id));
      }
      const isTailCluster = cluster.id === clusters[clusters.length - 1]?.id;
      const debounceDelay = isTailCluster ? 750 : 1200;
      const timer = setTimeout(() => {
        clusterDebounceTimersRef.current.delete(cluster.id);
        enqueueClusterOcr(cluster, { delay: 40 });
      }, debounceDelay);
      clusterDebounceTimersRef.current.set(cluster.id, timer);
    }
  }

  async function flushLiveLineOcr(options = {}) {
    if (ocrFrozenRef.current && !options.allowWhileFrozen) return;
    if (ocrBusyRef.current) {
      if (pendingOrderRef.current.length && (!ocrFrozenRef.current || options.allowWhileFrozen)) {
        queueLiveLineOcr(OCR_MIN_DELAY_MS, options);
      }
      return;
    }
    if (!pendingOrderRef.current.length) return;
    if (!currentAttemptId) return;

    ocrBusyRef.current = true;
    setLiveOcrStatus("Updating OCR...");

    let selectedLineId = null;
    let pendingOp = null;

    try {
      selectedLineId = pendingOrderRef.current[0];
      pendingOp = pendingLinesRef.current.get(selectedLineId);
      if (!pendingOp) {
        pendingOrderRef.current.shift();
        if (pendingOrderRef.current.length && (!ocrFrozenRef.current || options.allowWhileFrozen)) {
          queueLiveLineOcr(OCR_MIN_DELAY_MS, options);
        } else {
          setLiveOcrStatus("");
        }
        return;
      }

      pendingOrderRef.current.shift();

      const requestBody = {
        lineId: selectedLineId,
        lineOrder: Number.isFinite(Number(pendingOp?.lineOrder)) ? Number(pendingOp.lineOrder) : 0,
        revision: Number.isFinite(Number(pendingOp?.revision)) ? Number(pendingOp.revision) : 0
      };
      if (pendingOp.clearLine) requestBody.clearLine = true;
      else requestBody.dataUrl = pendingOp.dataUrl;

      const controller = new AbortController();
      const fetchTimer = setTimeout(() => controller.abort(), 28000);
      let response;
      try {
        response = await fetch(`${apiBase}/api/attempts/${currentAttemptId}/ocr-line`, {
          method: "POST",
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(requestBody)
        });
      } finally {
        clearTimeout(fetchTimer);
      }

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Live OCR failed for this row");
      }

      const payload = await response.json().catch(() => ({}));
      const stale = Boolean(payload?.ocr?.line?.stale);
      const confidence = String(payload?.ocr?.line?.confidence || "").toLowerCase();
      const contentKind = String(payload?.ocr?.line?.contentKind || "").toLowerCase();
      const lineLatex = String(payload?.ocr?.line?.latex || "");
      const lowQualityLine = confidence === "low" || /\[unclear\]/i.test(lineLatex);

      if (!stale && payload?.attempt) {
        applyAttemptState(payload.attempt, {
          replaceEditor: !ocrEditorDirtyRef.current && !lowQualityLine,
          preserveAnalysis: true
        });
        publishSharedAttemptState(payload.attempt);
      }

      setLiveOcrStatus(
        stale
          ? ""
          : confidence === "low"
            ? "Writing looks a bit unclear; final Analyze will review carefully."
            : contentKind === "diagram"
              ? "Understood your diagram."
              : contentKind === "mixed"
                ? "Read your math and diagram."
                : "Step saved."
      );

      const acknowledgedRevision = Number(payload?.ocr?.line?.revision ?? pendingOp.revision ?? 0);
      laneAckRevisionRef.current[selectedLineId] = Math.max(
        Number(laneAckRevisionRef.current[selectedLineId] || 0),
        acknowledgedRevision
      );

      const currentPending = pendingLinesRef.current.get(selectedLineId);
      if (currentPending === pendingOp) {
        pendingLinesRef.current.delete(selectedLineId);
      } else if (currentPending) {
        pendingOrderRef.current.push(selectedLineId);
      }
    } catch (err) {
      setLiveOcrStatus("Couldn't read that line clearly. Try rewriting.");
      if (selectedLineId) {
        delete clusterFingerprintsRef.current[selectedLineId];
        const currentPending = pendingLinesRef.current.get(selectedLineId);
        if (currentPending === pendingOp) {
          pendingLinesRef.current.delete(selectedLineId);
        }
        pendingOrderRef.current = pendingOrderRef.current.filter((id) => id !== selectedLineId);
      }
    } finally {
      ocrBusyRef.current = false;
      if (pendingOrderRef.current.length && (!ocrFrozenRef.current || options.allowWhileFrozen)) {
        queueLiveLineOcr(OCR_MIN_DELAY_MS, options);
      }
    }
  }

  async function flushAllPendingLiveOcr() {
    const startedAt = Date.now();
    while (Date.now() - startedAt < 90000) {
      if (!pendingOrderRef.current.length && !ocrBusyRef.current) break;
      if (!ocrBusyRef.current && pendingOrderRef.current.length) {
        // eslint-disable-next-line no-await-in-loop
        await flushLiveLineOcr({ allowWhileFrozen: true });
      } else {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 40));
      }
    }
  }

  async function syncCommittedStrokes(strokes) {
    const cleanStrokes = normalizeStrokes(strokes);
    if (!cleanStrokes.length || !roomDocRef.current) return;
    const backupPayload = {
      strokes: cleanStrokes,
      stageWidth: Math.ceil(stageMetricsRef.current.width || stageCanvasWidth),
      stageHeight: Math.ceil(stageMetricsRef.current.height || stageHeight)
    };
    beginCanvasSync();
    try {
      appendCommittedStrokes(roomDocRef.current, cleanStrokes, {
        stageWidth: backupPayload.stageWidth,
        stageHeight: backupPayload.stageHeight
      });
      applySharedRoomState({ forceCanvas: true });
      void persistCanvasBackup("/strokes", backupPayload);
    } catch (err) {
      setNotice(err.message || "Shared stroke sync failed");
    } finally {
      endCanvasSync();
    }
  }

  async function syncRemoveStroke(strokeId) {
    if (!roomDocRef.current || !strokeId) return;
    beginCanvasSync();
    try {
      removeCommittedStroke(roomDocRef.current, strokeId, {
        stageWidth: Math.ceil(stageMetricsRef.current.width || stageCanvasWidth),
        stageHeight: Math.ceil(stageMetricsRef.current.height || stageHeight)
      });
      applySharedRoomState({ forceCanvas: true });
      void persistCanvasBackup("/canvas", {
        action: "removeStroke",
        strokeId
      });
    } catch (err) {
      setNotice(err.message || "Undo sync failed");
    } finally {
      endCanvasSync();
    }
  }

  async function syncLaneHeightsToRoom(nextHeights, options = {}) {
    if (!roomDocRef.current) return;
    const laneHeightsKey = serializeLaneHeights(nextHeights);
    if (!options.force && laneHeightsKey === lastLaneSyncKeyRef.current) return;

    beginCanvasSync();
    try {
      replaceLaneHeights(roomDocRef.current, sanitizeLaneHeights(nextHeights), {
        stageWidth: Math.ceil(stageMetricsRef.current.width || stageCanvasWidth),
        stageHeight: Math.ceil(stageMetricsRef.current.height || stageHeight)
      });
      lastLaneSyncKeyRef.current = laneHeightsKey;
      applySharedRoomState({ forceCanvas: true });
      void persistCanvasBackup("/canvas", {
        action: "laneHeights",
        laneHeights: sanitizeLaneHeights(nextHeights)
      });
    } catch (err) {
      laneLocalChangeRef.current = true;
      setNotice(err.message || "Lane resize sync failed");
    } finally {
      endCanvasSync();
    }
  }

  async function syncAttemptClear() {
    if (!currentAttemptId) return;
    try {
      const response = await fetch(`${apiBase}/api/attempts/${currentAttemptId}/ocr-line`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ clearAll: true })
      });
      if (!response.ok) return;
      const payload = await response.json().catch(() => ({}));
      if (payload?.attempt) {
        applyAttemptState(payload.attempt, { replaceEditor: true, preserveAnalysis: false });
        publishSharedAttemptState(payload.attempt);
      }
    } catch (err) {
      // Ignore OCR clear failures; the room clear itself already succeeded locally.
    }
  }

  async function syncClearCanvas(nextHeights) {
    if (!roomDocRef.current) return;
    beginCanvasSync();
    try {
      const clearedAttempt = buildClearedAttemptState();
      clearSharedCanvas(roomDocRef.current, sanitizeLaneHeights(nextHeights), {
        stageWidth: Math.ceil(stageMetricsRef.current.width || stageCanvasWidth),
        stageHeight: Math.ceil(stageMetricsRef.current.height || stageHeight)
      }, {
        editorLatex: "",
        attemptState: clearedAttempt
      });
      applySharedRoomState({ forceCanvas: true });
      void persistCanvasBackup("/canvas", {
        action: "clear",
        laneHeights: sanitizeLaneHeights(nextHeights)
      });
      await syncAttemptClear();
    } catch (err) {
      setNotice(err.message || "Shared clear failed");
    } finally {
      endCanvasSync();
    }
  }

  useEffect(() => {
    if (loading || laneDragBoundary !== null || !laneLocalChangeRef.current) return undefined;
    if (laneSyncTimerRef.current) clearTimeout(laneSyncTimerRef.current);
    laneSyncTimerRef.current = setTimeout(() => {
      laneSyncTimerRef.current = null;
      if (!laneLocalChangeRef.current) return;
      const heightsSnapshot = laneHeightsRef.current.slice();
      laneLocalChangeRef.current = false;
      void syncLaneHeightsToRoom(heightsSnapshot);
    }, 160);

    return () => {
      if (laneSyncTimerRef.current) clearTimeout(laneSyncTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laneDragBoundary, laneHeights, loading]);

  const syncActiveStrokeCanvas = () => {
    const canvas = activeStrokeCanvasRef.current;
    if (!canvas) return null;
    const width = Math.max(1, Math.ceil(stageMetricsRef.current.width || stageCanvasWidth));
    const height = Math.max(1, Math.ceil(stageMetricsRef.current.height || stageHeight));
    const dpr = typeof window !== "undefined" ? Math.max(1, Number(window.devicePixelRatio) || 1) : 1;

    if (canvas.width !== Math.round(width * dpr) || canvas.height !== Math.round(height * dpr)) {
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.imageSmoothingEnabled = true;
    return ctx;
  };

  function clearActiveStrokePreview() {
    const ctx = syncActiveStrokeCanvas();
    if (!ctx) return;
    ctx.clearRect(0, 0, Math.ceil(stageMetricsRef.current.width || stageCanvasWidth), Math.ceil(stageMetricsRef.current.height || stageHeight));
  }

  function drawActiveStrokePreview() {
    const ctx = syncActiveStrokeCanvas();
    const meta = activeStrokeMetaRef.current;
    const points = activeStrokePointsRef.current;
    if (!ctx) return;
    const width = Math.ceil(stageMetricsRef.current.width || stageCanvasWidth);
    const height = Math.ceil(stageMetricsRef.current.height || stageHeight);
    ctx.clearRect(0, 0, width, height);
    if (!meta || meta.tool !== "pen" || points.length < 2) return;

    ctx.save();
    ctx.strokeStyle = meta.color || strokeColor;
    ctx.fillStyle = meta.color || strokeColor;
    ctx.lineWidth = meta.width || 2.5;
    if (points.length === 2) {
      ctx.beginPath();
      ctx.arc(points[0], points[1], Math.max(1, (meta.width || 2.5) / 2), 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }
    ctx.beginPath();
    ctx.moveTo(points[0], points[1]);
    for (let i = 2; i < points.length; i += 2) {
      ctx.lineTo(points[i], points[i + 1]);
    }
    ctx.stroke();
    ctx.restore();
  }

  useEffect(() => {
    const ctx = syncActiveStrokeCanvas();
    if (!ctx) return;
    if (activeStrokeMetaRef.current?.tool === "pen" && activeStrokePointsRef.current.length) {
      drawActiveStrokePreview();
    } else {
      clearActiveStrokePreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laneHeights, stageCanvasWidth, stageHeight, slateFullScreen]);

  useEffect(() => {
    if (strokeTool !== "scroll") return;
    const canvas = activeStrokeCanvasRef.current;
    const ctx = canvas?.getContext?.("2d");
    if (ctx) ctx.clearRect(0, 0, canvas.width || 0, canvas.height || 0);
    activeStrokeMetaRef.current = null;
    activeStrokePointsRef.current = [];
    activeStrokePointProcessorRef.current = null;
    activeLaneRef.current = null;
    pointerIdRef.current = null;
    pointerOwnershipRef.current.reset();
    setIsDrawing(false);
  }, [strokeTool]);

  function getSlateInteractionRect() {
    const overlayRect = activeStrokeCanvasRef.current?.getBoundingClientRect?.();
    if (overlayRect?.width && overlayRect?.height) return overlayRect;
    const stageRect = stageRef.current?.container?.()?.getBoundingClientRect?.();
    if (stageRect?.width && stageRect?.height) return stageRect;
    const paneRect = canvasPaneRef.current?.getBoundingClientRect?.();
    return paneRect?.width && paneRect?.height ? paneRect : null;
  }

  function getSlatePointFromClient(clientX, clientY) {
    const rect = getSlateInteractionRect();
    if (!rect || !Number.isFinite(clientX) || !Number.isFinite(clientY)) return null;
    const width = Math.max(1, Number(stageMetricsRef.current.width || stageCanvasWidth));
    const height = Math.max(1, Number(stageMetricsRef.current.height || stageHeight));
    const scaleX = rect.width > 0 ? width / rect.width : 1;
    const scaleY = rect.height > 0 ? height / rect.height : 1;
    const x = Math.max(0, Math.min(
      width,
      (clientX - rect.left + Number(profileConfig.nibOffsetX || 0)) * scaleX
    ));
    const y = Math.max(0, Math.min(
      height,
      (clientY - rect.top + Number(profileConfig.nibOffsetY || 0)) * scaleY
    ));
    return { x, y };
  }

  function getLaneFromPointerEvent(pointerEvent) {
    const pos = getSlatePointFromClient(pointerEvent?.clientX, pointerEvent?.clientY);
    if (!pos) return null;
    const bounds = getLaneBounds(laneHeightsRef.current);
    const laneIndex = getLaneIndexByY(pos.y, bounds);
    const lane = bounds.find((item) => item.index === laneIndex) || null;
    if (!lane) return null;
    return { pos, laneIndex, lane };
  }

  function appendPoint(x, y) {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
    const points = activeStrokePointsRef.current;
    if (points.length >= 2) {
      const lastX = Number(points[points.length - 2]);
      const lastY = Number(points[points.length - 1]);
      const dx = x - lastX;
      const dy = y - lastY;
      const minDistance = Number(profileConfig.minPointDistance || 0.75);
      if (dx * dx + dy * dy < minDistance * minDistance) return false;
    }
    points.push(x, y);
    return true;
  }

  function updateEraserStrokePreview() {
    const meta = activeStrokeMetaRef.current;
    if (!meta || meta.tool !== "eraser") return;
    const committedPoints =
      activeStrokePointsRef.current.length >= 4
        ? activeStrokePointsRef.current.slice()
        : activeStrokePointsRef.current.length >= 2
          ? [
            activeStrokePointsRef.current[0],
            activeStrokePointsRef.current[1],
            activeStrokePointsRef.current[0] + 0.01,
            activeStrokePointsRef.current[1] + 0.01
          ]
          : [];
    setLines((prev) => {
      const next = prev.map((stroke) =>
        stroke.id === meta.id
          ? {
            ...stroke,
            points: committedPoints
          }
          : stroke
      );
      linesRef.current = next;
      return next;
    });
  }

  function appendPointerSamples(nativeEvent, lane, options = {}) {
    if (!nativeEvent || !lane) return;
    const events = options.final
      ? [nativeEvent]
      : getCoalescedPointerSamples(nativeEvent, profileConfig.coalescedLimit);
    let changed = false;
    events.forEach((pointEvent) => {
      const pos = getSlatePointFromClient(pointEvent.clientX, pointEvent.clientY);
      if (!pos) return;
      const rawY = clampYToLane(pos.y, lane);
      const processed = activeStrokePointProcessorRef.current?.process(
        pos.x,
        rawY,
        pointEvent.timeStamp,
        { final: Boolean(options.final) }
      );
      const nextX = Number(processed?.x ?? pos.x);
      const nextY = clampYToLane(Number(processed?.y ?? rawY), lane);
      const points = activeStrokePointsRef.current;
      const previousX = Number(points[points.length - 2]);
      const previousY = Number(points[points.length - 1]);
      const segmentLength = Number(profileConfig.interpolationSegmentLength || 0);
      if (segmentLength > 0 && Number.isFinite(previousX) && Number.isFinite(previousY)) {
        const dx = nextX - previousX;
        const dy = nextY - previousY;
        const distance = Math.hypot(dx, dy);
        const segmentCount = Math.min(12, Math.max(0, Math.floor(distance / segmentLength) - 1));
        for (let index = 1; index <= segmentCount; index += 1) {
          const ratio = index / (segmentCount + 1);
          if (appendPoint(previousX + dx * ratio, previousY + dy * ratio)) changed = true;
        }
      }
      if (appendPoint(nextX, nextY)) changed = true;
    });
    if (!changed) return;
    if (activeStrokeMetaRef.current?.tool === "pen") {
      drawActiveStrokePreview();
      queuePreviewBroadcast();
      return;
    }
    updateEraserStrokePreview();
    const meta = activeStrokeMetaRef.current;
    if (meta) {
      publishPreviewStroke({
        ...meta,
        points:
          activeStrokePointsRef.current.length >= 4
            ? activeStrokePointsRef.current.slice()
            : activeStrokePointsRef.current.length >= 2
              ? [
                  activeStrokePointsRef.current[0],
                  activeStrokePointsRef.current[1],
                  activeStrokePointsRef.current[0] + 0.01,
                  activeStrokePointsRef.current[1] + 0.01
                ]
              : []
      });
    }
  }

  function finalizeActiveStroke() {
    const meta = activeStrokeMetaRef.current;
    const rawPoints = activeStrokePointsRef.current.slice();
    if (previewFrameRef.current) {
      window.cancelAnimationFrame(previewFrameRef.current);
      previewFrameRef.current = null;
    }
    clearActiveStrokePreview();
    resetActiveStroke();
    if (!meta || rawPoints.length < 2) return null;

    const points = rawPoints.length >= 4 ? rawPoints : [rawPoints[0], rawPoints[1], rawPoints[0] + 0.01, rawPoints[1] + 0.01];
    if (meta.tool === "pen") {
      const stroke = { ...meta, points };
      const next = [...linesRef.current, stroke];
      linesRef.current = next;
      setLines(next);
      return stroke;
    }

    const stroke = { ...meta, points };
    setLines((prev) => {
      const next = prev.map((item) => (item.id === meta.id ? stroke : item));
      linesRef.current = next;
      return next;
    });
    return stroke;
  }

  function beginOwnedCollaborationStroke(action, captureTarget = null) {
    const samples = Array.isArray(action?.samples) ? action.samples : [];
    const firstSample = samples[0];
    const laneInfo = getLaneFromPointerEvent(firstSample);
    if (!firstSample || !laneInfo || laneDragRef.current) return false;
    if (action.replacedPointerId !== null && action.replacedPointerId !== undefined) {
      const replacedStroke = activeStrokeMetaRef.current;
      if (replacedStroke?.tool === "eraser") {
        const next = linesRef.current.filter((stroke) => stroke.id !== replacedStroke.id);
        linesRef.current = next;
        setLines(next);
      }
      if (previewFrameRef.current) {
        window.cancelAnimationFrame(previewFrameRef.current);
        previewFrameRef.current = null;
      }
      clearActiveStrokePreview();
      clearPreviewStroke();
      resetActiveStroke();
      try {
        captureTarget?.releasePointerCapture?.(action.replacedPointerId);
      } catch (err) {
        // The browser can release the provisional palm capture itself.
      }
    }
    clearTutorHint();
    setShowClearConfirm(false);
    if (!ocrFrozenRef.current && laneInfo.laneIndex > 0) {
      enqueueDirtyLanes(laneInfo.laneIndex - 1, { delay: 90 });
    }

    pointerIdRef.current = action.pointerId;
    activeLaneRef.current = laneInfo.laneIndex;
    setIsDrawing(true);
    setRedoStack([]);
    const snapY = clampYToLane(laneInfo.pos.y, laneInfo.lane);
    const strokeId = `stroke_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const nextStroke = {
      id: strokeId,
      strokeId,
      userId: String(session?.currentParticipant?.userId || ""),
      laneIndex: laneInfo.laneIndex,
      tool: strokeTool,
      color: strokeColor,
      width: strokeTool === "eraser" ? 28 : profileConfig.lineWidth
    };
    activeStrokeMetaRef.current = nextStroke;
    activeStrokePointProcessorRef.current = strokeTool === "pen"
      ? createInkPointProcessor(profileConfig)
      : null;
    const initialPoint = activeStrokePointProcessorRef.current?.process(
      laneInfo.pos.x,
      snapY,
      firstSample.timeStamp
    );
    activeStrokePointsRef.current = [
      Number(initialPoint?.x ?? laneInfo.pos.x),
      Number(initialPoint?.y ?? snapY)
    ];

    if (captureTarget?.setPointerCapture && Number.isFinite(Number(action.pointerId))) {
      try {
        captureTarget.setPointerCapture(action.pointerId);
      } catch (err) {
        // Ignore capture failures.
      }
    }

    if (strokeTool === "pen") {
      drawActiveStrokePreview();
      queuePreviewBroadcast();
      samples.slice(1).forEach((sample) => appendPointerSamples(sample, laneInfo.lane));
      return true;
    }

    const eraserStroke = {
      ...nextStroke,
      points: [laneInfo.pos.x, snapY, laneInfo.pos.x + 0.01, snapY + 0.01]
    };
    const next = [...linesRef.current, eraserStroke];
    linesRef.current = next;
    setLines(next);
    publishPreviewStroke(eraserStroke);
    samples.slice(1).forEach((sample) => appendPointerSamples(sample, laneInfo.lane));
    return true;
  }

  function handlePointerDown(event) {
    if (strokeTool === "scroll" || laneDragRef.current) return;
    const nativeEvent = event?.evt;
    if (!nativeEvent) return;
    nativeEvent.preventDefault?.();
    const action = pointerOwnershipRef.current.pointerDown(nativeEvent);
    if (action.type === "start") {
      beginOwnedCollaborationStroke(action, nativeEvent.target);
    }
  }

  function handlePointerMove(event) {
    const nativeEvent = event?.evt;
    if (!nativeEvent) return;
    const action = pointerOwnershipRef.current.pointerMove(nativeEvent);
    if (action.type === "start") {
      nativeEvent.preventDefault?.();
      beginOwnedCollaborationStroke(action, nativeEvent.target);
      return;
    }
    if (action.type !== "move" || action.pointerId !== pointerIdRef.current) return;
    nativeEvent.preventDefault?.();
    const laneIndex = Number(activeLaneRef.current);
    const lane = getLaneBounds(laneHeightsRef.current).find((item) => item.index === laneIndex);
    appendPointerSamples(nativeEvent, lane);
  }

  function handlePointerUp(event, options = {}) {
    const nativeEvent = event?.evt;
    const pointerId = Number.isFinite(Number(nativeEvent?.pointerId)) ? Number(nativeEvent.pointerId) : null;
    const action = pointerOwnershipRef.current.pointerUp(nativeEvent, options);
    if (nativeEvent?.target?.releasePointerCapture && pointerId !== null) {
      try {
        nativeEvent.target.releasePointerCapture(pointerId);
      } catch (err) {
        // Ignore capture failures.
      }
    }
    if (action.type === "tap") {
      if (!beginOwnedCollaborationStroke(action, nativeEvent?.target)) return;
    } else if (
      !["end", "cancel"].includes(action.type) ||
      action.pointerId !== pointerIdRef.current
    ) {
      return;
    }
    const laneIndex = Number(activeLaneRef.current);
    const lane = getLaneBounds(laneHeightsRef.current).find((item) => item.index === laneIndex);
    if (lane && action.type !== "cancel" && activeStrokeMetaRef.current?.tool === "pen") {
      appendPointerSamples(nativeEvent, lane, { final: true });
    }
    setIsDrawing(false);
    const stroke = finalizeActiveStroke();
    clearPreviewStroke();
    if (stroke) {
      void syncCommittedStrokes([stroke]);
    }
    setTimeout(() => {
      onSlateStrokeFinished();
    }, 0);
  }

  function handleRuleDragStart(boundaryIndex, event) {
    event.preventDefault();
    event.stopPropagation();
    const idx = Number(boundaryIndex);
    const heights = laneHeightsRef.current;
    if (!Number.isFinite(idx) || idx < 0 || idx >= heights.length - 1) return;
    const bounds = getLaneBounds(heights);
    const upperLane = bounds.find((item) => item.index === idx);
    if (!upperLane) return;

    let minDelta = MIN_LANE_HEIGHT - upperLane.height;
    let maxDelta = Number.POSITIVE_INFINITY;
    const upperInk = getLaneInkExtents(idx);
    if (upperInk) {
      const upperInkBottomY = upperInk.laneTop + upperInk.maxY;
      minDelta = Math.max(minDelta, upperInkBottomY + 10 - upperLane.bottom);
    }
    bounds.forEach((lane) => {
      if (lane.index <= idx) return;
      const ink = getLaneInkExtents(lane.index);
      if (!ink) return;
      const inkTopY = ink.laneTop + ink.minY;
      const inkBottomY = ink.laneTop + ink.maxY;
      minDelta = Math.max(minDelta, inkBottomY + 10 - lane.bottom);
      maxDelta = Math.min(maxDelta, inkTopY - 10 - lane.top);
    });

    if (event.currentTarget?.setPointerCapture && Number.isFinite(Number(event.pointerId))) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (err) {
        // Ignore capture failures.
      }
    }

    laneDragRef.current = {
      boundaryIndex: idx,
      startY: Number(event.clientY || 0),
      startHeight: Number(heights[idx] || DEFAULT_LANE_HEIGHT),
      minDelta,
      maxDelta
    };
    setLaneDragBoundary(idx);
    setShowClearConfirm(false);
  }

  useEffect(() => {
    function onMove(event) {
      const drag = laneDragRef.current;
      if (!drag) return;
      if (event.preventDefault) event.preventDefault();
      const deltaY = Number(event.clientY || 0) - drag.startY;
      const bounds = getLaneBounds(laneHeightsRef.current);
      const upperLane = bounds.find((item) => item.index === drag.boundaryIndex);
      if (!upperLane) return;
      const clampedDelta = Math.max(
        Number(drag.minDelta || MIN_LANE_HEIGHT - upperLane.height),
        Math.min(Number(drag.maxDelta || Number.POSITIVE_INFINITY), deltaY)
      );
      const nextHeight = Math.max(MIN_LANE_HEIGHT, Math.round(drag.startHeight + clampedDelta));
      setLaneHeights((prev) => {
        if (drag.boundaryIndex >= prev.length) return prev;
        const currentHeight = Number(prev[drag.boundaryIndex] || DEFAULT_LANE_HEIGHT);
        if (currentHeight === nextHeight) return prev;
        const next = [...prev];
        next[drag.boundaryIndex] = nextHeight;
        laneLocalChangeRef.current = true;
        return next;
      });
    }

    function onUp() {
      if (!laneDragRef.current) return;
      laneDragRef.current = null;
      setLaneDragBoundary(null);
      const bounds = getLaneBounds(laneHeightsRef.current);
      const firstEmptyLane = bounds.find((lane) => !laneInkStateRef.current[getLineIdForLane(lane.index)]);
      setTutorLaneIndex(firstEmptyLane ? firstEmptyLane.index : bounds.length ? bounds[bounds.length - 1].index : 0);
    }

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, []);

  function handleUndoStroke() {
    clearTutorHint();
    setLines((prev) => {
      const removed = prev[prev.length - 1];
      if (!removed) return prev;
      setRedoStack((stack) => [...stack, removed]);
      const next = prev.slice(0, -1);
      linesRef.current = next;
      if (Number.isFinite(Number(removed.laneIndex))) {
        const laneIndex = Number(removed.laneIndex);
        const lineId = getLineIdForLane(laneIndex);
        const hadInk = Boolean(laneInkStateRef.current[lineId]);
        const nowHasInk = laneHasPenInk(laneIndex, next, laneHeightsRef.current);
        laneInkStateRef.current[lineId] = nowHasInk;
        if (hadInk || nowHasInk) {
          markLaneDirty(laneIndex);
        }
        syncTutorLaneWithInk(next, laneHeightsRef.current);
      }
      void syncRemoveStroke(removed.strokeId || removed.id);
      setTimeout(() => {
        onSlateStrokeFinished();
      }, 0);
      return next;
    });
  }

  function handleRedoStroke() {
    clearTutorHint();
    setRedoStack((stack) => {
      const stroke = stack[stack.length - 1];
      if (!stroke) return stack;
      const remaining = stack.slice(0, -1);
      setLines((prev) => {
        const next = [...prev, stroke];
        linesRef.current = next;
        setTimeout(() => {
          onSlateStrokeFinished();
        }, 0);
        return next;
      });
      void syncCommittedStrokes([stroke]);
      return remaining;
    });
  }

  async function finalizePendingLiveOcr() {
    if (activeStrokeMetaRef.current) {
      setIsDrawing(false);
      const stroke = finalizeActiveStroke();
      pointerOwnershipRef.current.reset();
      if (stroke) {
        await syncCommittedStrokes([stroke]);
      }
    }

    // Flush any pending cluster debounce timers immediately
    if (clusterDebounceTimersRef.current && clusterDebounceTimersRef.current.size > 0) {
      clusterDebounceTimersRef.current.forEach((t) => clearTimeout(t));
      clusterDebounceTimersRef.current.clear();
    }

    const clusters = syncInkClusters();
    clusters.forEach((cluster) => {
      const fingerprint = getClusterFingerprint(cluster);
      const ackRevision = Number(laneAckRevisionRef.current[cluster.id] || 0);
      const clusterRevision = Number(cluster.revision || 1);
      const isAcknowledged = ackRevision >= clusterRevision;
      const isUnchanged = clusterFingerprintsRef.current[cluster.id] === fingerprint;
      const isPending = pendingLinesRef.current.has(cluster.id);

      if (!isAcknowledged || !isUnchanged || isPending) {
        enqueueClusterOcr(cluster, { delay: 10, allowWhileFrozen: true, force: true });
      }
    });

    await flushAllPendingLiveOcr();
  }

  async function clearCanvas(syncServer = true) {
    setBusyAction("");
    setShowClearConfirm(false);
    setTutorChatOpen(false);
    setTutorHintHistory([]);
    clearTutorHint();
    setNotice("");
    setRedoStack([]);
    setLines([]);
    linesRef.current = [];
    setStepClusters([]);
    stepClustersRef.current = [];
    clusterFingerprintsRef.current = {};
    clusterDebounceTimersRef.current.forEach((t) => clearTimeout(t));
    clusterDebounceTimersRef.current.clear();
    previousMathClustersCountRef.current = 0;
    const nextHeights = buildLaneHeights();
    setLaneHeights(nextHeights);
    laneHeightsRef.current = nextHeights;
    laneLocalChangeRef.current = false;
    lastLaneSyncKeyRef.current = serializeLaneHeights(nextHeights);
    setLaneDragBoundary(null);
    setTutorLaneIndex(0);
    setLiveOcrStatus("Shared slate cleared");
    setOcrLaneRows([]);
    updateOcrEditor("", { fromServer: true });
    setOcrReady(false);
    setAnalysisStatus("");
    setAnalysisFeedback("");
    setAnalysisData(null);
    setAnalysisAlignment([]);
    setAnalysisFinalCorrect(null);
    setAnalysisLaneMap([]);
    setSelectedDiagStep(null);
    pendingLinesRef.current = new Map();
    pendingOrderRef.current = [];
    laneRevisionRef.current = {};
    laneAckRevisionRef.current = {};
    laneInkStateRef.current = {};
    clearActiveStrokePreview();
    resetActiveStroke();
    pointerOwnershipRef.current.reset();
    setIsDrawing(false);
    if (slateRef.current) {
      slateRef.current.scrollTo({ left: 0, top: 0, behavior: "auto" });
    }
    if (syncServer) {
      await syncClearCanvas(nextHeights);
    }
  }

  async function handleAnalyze() {
    if (!currentAttemptId || isAnalyzing || isHinting) return;
    clearTutorHint();
    setBusyAction("analyze");
    setShowClearConfirm(false);
    setNotice("");
    setAnalysisStatus("Running fast tutor analysis...");
    setAnalysisFeedback("");
    setAnalysisData(null);
    setAnalysisAlignment([]);
    setAnalysisFinalCorrect(null);
    if (clusterDebounceTimersRef.current && clusterDebounceTimersRef.current.size > 0) {
      clusterDebounceTimersRef.current.forEach((timer, clusterId) => {
        clearTimeout(timer);
        const cluster = (stepClustersRef.current || []).find((c) => c.id === clusterId);
        if (cluster) {
          enqueueClusterOcr(cluster, { delay: 0, force: true, allowWhileFrozen: true });
        }
      });
      clusterDebounceTimersRef.current.clear();
    }
    setOcrFrozen(true);
    setLiveOcrStatus("");

    try {
      await finalizePendingLiveOcr();
      let finalizedLatex = "";
      const finalizeRes = await fetch(`${apiBase}/api/attempts/${currentAttemptId}/ocr-live/finalize`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          activeLineIds: (stepClustersRef.current || []).map((c) => c.id)
        })
      }).catch(() => null);
      if (finalizeRes?.ok) {
        const finalizePayload = await finalizeRes.json().catch(() => ({}));
        finalizedLatex = String(finalizePayload?.ocr?.editedLatex || "");
      }

      const localLatex = String(ocrEditedLatexRef.current || "");
      const isSlateTab = workTab === "slate";
      const finalLatex = isSlateTab && finalizedLatex ? finalizedLatex : String(localLatex || finalizedLatex || "");
      if (finalizedLatex && (isSlateTab || !localLatex)) {
        updateOcrEditor(finalizedLatex, { fromServer: true });
        setOcrReady(Boolean(finalizedLatex.trim()));
      }
      if (!finalLatex.trim()) {
        const hasAnySlateInk = (linesRef.current || []).some(
          (stroke) => Array.isArray(stroke?.points) && stroke.points.length >= 2
        );
        if (!hasAnySlateInk) throw new Error("No work available to analyze");
      }

      const confirmRes = await fetch(`${apiBase}/api/attempts/${currentAttemptId}/ocr`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          editedLatex: finalLatex,
          editedSteps: splitLatexSteps(finalLatex),
          confirm: true
        })
      });
      if (!confirmRes.ok) {
        const body = await confirmRes.json().catch(() => ({}));
        throw new Error(body.error || "Failed to confirm OCR");
      }
      const confirmPayload = await confirmRes.json().catch(() => ({}));
      if (confirmPayload?.attempt) {
        applyAttemptState(confirmPayload.attempt, { replaceEditor: true, preserveAnalysis: true });
        publishSharedAttemptState(confirmPayload.attempt);
      }

      const analyzeRes = await fetch(`${apiBase}/api/attempts/${currentAttemptId}/analyze-equivalence`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      });
      if (!analyzeRes.ok) {
        const body = await analyzeRes.json().catch(() => ({}));
        const details =
          body?.details?.error?.message ||
          body?.details?.message ||
          JSON.stringify(body?.details || {});
        throw new Error(details || body.error || "Analysis failed");
      }

      const analyzePayload = await analyzeRes.json().catch(() => ({}));
      if (analyzePayload?.attempt) {
        applyAttemptState(analyzePayload.attempt, { replaceEditor: true, preserveAnalysis: false });
        publishSharedAttemptState(analyzePayload.attempt);
      }
      setAnalysisStatus("Analysis complete");
      setNotice("Analysis updated for everyone in this room.");
    } catch (err) {
      setAnalysisStatus("Failed");
      setAnalysisFeedback(err.message || "Analysis failed");
      setAnalysisData(null);
      setAnalysisAlignment([]);
      setAnalysisFinalCorrect(null);
      setSelectedDiagStep(null);
    } finally {
      setOcrFrozen(false);
      setBusyAction("");
    }
  }

  async function handleRequestHint(options = {}) {
    const hintIntent = options?.intent || "next_step";
    if (!currentAttemptId || isAnalyzing || isHinting) return;
    setBusyAction("hint");
    setHintingIntent(hintIntent);
    setShowClearConfirm(false);
    setTutorChatOpen(true);
    clearTutorHint();
    setNotice("");
    if (clusterDebounceTimersRef.current && clusterDebounceTimersRef.current.size > 0) {
      clusterDebounceTimersRef.current.forEach((timer, clusterId) => {
        clearTimeout(timer);
        const cluster = (stepClustersRef.current || []).find((c) => c.id === clusterId);
        if (cluster) {
          enqueueClusterOcr(cluster, { delay: 0, force: true, allowWhileFrozen: true });
        }
      });
      clusterDebounceTimersRef.current.clear();
    }
    setOcrFrozen(true);

    try {
      await finalizePendingLiveOcr();
      let finalizedLatex = "";
      const finalizeRes = await fetch(`${apiBase}/api/attempts/${currentAttemptId}/ocr-live/finalize`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          activeLineIds: (stepClustersRef.current || []).map((c) => c.id)
        })
      }).catch(() => null);
      if (finalizeRes?.ok) {
        const finalizePayload = await finalizeRes.json().catch(() => ({}));
        finalizedLatex = String(finalizePayload?.ocr?.editedLatex || "");
      }

      const localLatex = String(ocrEditedLatexRef.current || "");
      const isSlateTab = workTab === "slate";
      const nextLatex = isSlateTab && finalizedLatex ? finalizedLatex : (localLatex || finalizedLatex || "");
      if (finalizedLatex && (isSlateTab || !localLatex)) {
        updateOcrEditor(finalizedLatex, { fromServer: true });
        setOcrReady(Boolean(finalizedLatex.trim()));
      }

      const syncRes = await fetch(`${apiBase}/api/attempts/${currentAttemptId}/ocr`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          editedLatex: nextLatex,
          editedSteps: splitLatexSteps(nextLatex)
        })
      });
      if (!syncRes.ok) {
        const body = await syncRes.json().catch(() => ({}));
        throw new Error(body.error || "Failed to sync current OCR text");
      }
      const syncPayload = await syncRes.json().catch(() => ({}));
      if (syncPayload?.attempt) {
        applyAttemptState(syncPayload.attempt, { replaceEditor: true, preserveAnalysis: true });
        publishSharedAttemptState(syncPayload.attempt);
      }

      const previousHintsPayload = (Array.isArray(tutorHintHistory) ? tutorHintHistory : [])
        .slice(-4)
        .map((entry) => {
          const t = entry?.tutor || {};
          return {
            title: t?.summary?.title || t?.guidance?.hint?.title || "",
            message: t?.summary?.message || t?.guidance?.hint?.message || "",
            status: entry?.status || "Hint"
          };
        })
        .filter((h) => h.title || h.message);

      const tutorRes = await fetch(`${apiBase}/api/attempts/${currentAttemptId}/tutor-evaluate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          previousHints: previousHintsPayload,
          intent: hintIntent
        })
      });
      if (!tutorRes.ok) {
        const body = await tutorRes.json().catch(() => ({}));
        const details = body?.details?.error?.message || body?.details?.message || "";
        throw new Error(details || body.error || "Hint request failed");
      }

      const tutorPayload = await tutorRes.json().catch(() => ({}));
      if (tutorPayload?.attempt) {
        applyAttemptState(tutorPayload.attempt, { replaceEditor: true, preserveAnalysis: true });
        publishSharedAttemptState(tutorPayload.attempt);
      }
      const tutor = tutorPayload?.tutor || null;
      const nextTutorStatus = tutor?.guidance?.showError && tutor?.guidance?.error
        ? "Error"
        : tutor?.studentState === "complete"
          ? "Done"
          : "Hint";
      setTutorHintData(tutor);
      rememberTutorHint(nextTutorStatus, tutor, { intent: hintIntent });
      setNotice("Tutor guidance refreshed for your current OCR text.");
    } catch (err) {
      const fallbackTutor = {
        mode: "info",
        summary: {
          title: "Hint unavailable",
          message: err.message || "We could not prepare a hint right now.",
          followUp: null
        },
        targetNode: null,
        blockingIssue: null,
        progress: null
      };
      setTutorHintData(fallbackTutor);
      rememberTutorHint("Error", fallbackTutor, { intent: hintIntent });
    } finally {
      setOcrFrozen(false);
      setBusyAction("");
      setHintingIntent(null);
    }
  }

  function renderAnalyzeActions(extraClassName = "") {
    return (
      <div className={`analysis-runner ${extraClassName}`.trim()}>
        <div className="analysis-runner-actions">
          <button
            id="slate-btn-analyze"
            className="primary-button"
            type="button"
            onClick={handleAnalyze}
            disabled={isAnalyzing || isHinting}
          >
            {isAnalyzing ? "Analyzing..." : "Analyze"}
          </button>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <section className="page collaboration-page">
        <h2 className="page-title">Loading collaboration room...</h2>
      </section>
    );
  }

  if (loading && !question) {
    return (
      <section className="page collaboration-page">
        <h2 className="page-title">Loading collaboration room...</h2>
      </section>
    );
  }

  if (error && !question) {
    return (
      <section className="page collaboration-page">
        <h2 className="page-title">Unable to load collaboration room</h2>
        <p className="error-text">{error}</p>
      </section>
    );
  }

  return (
    <section className="page collaboration-page collaboration-focus-page">
      <audio ref={remoteAudioRef} autoPlay playsInline className="collab-remote-audio" />

      <div className="collab-toolbar collab-room-topbar">
        <div className="collab-room-topbar-row">
          <div className="collab-room-brand-wrap">
            <div className="collab-room-brand">Ink Together</div>
          </div>

          <div className="collab-room-chip-strip">
            <div className="collab-participants collab-participants-inline">
              {(Array.isArray(session?.participants) ? session.participants : []).map((participant) => {
                const isSelf = String(participant?.userId || "") === String(session?.currentParticipant?.userId || "");
                const participantId = String(participant?.userId || "");
                const participantVoiceState = isSelf
                  ? { joined: voiceJoined, muted: voiceMuted }
                  : voiceParticipantStates[participantId] || null;
                const participantVoiceLevel = isSelf
                  ? (voiceMuted ? 0 : localVoiceLevel)
                  : participantId === remoteParticipantId && !participantVoiceState?.muted
                    ? remoteVoiceLevel
                    : 0;
                const participantSpeaking = isSelf
                  ? localSpeaking
                  : Boolean(participantVoiceState?.joined) && !participantVoiceState?.muted && participantVoiceLevel >= 0.18;
                const participantVoiceTitle = isSelf
                  ? (voiceError || voiceStatus)
                  : participantVoiceState?.joined
                    ? (participantVoiceState.muted ? "Muted" : `${participant.name || "Student"} is on voice`)
                    : "Voice not joined";

                return (
                  <div key={participant.userId} className={`collab-participant-chip ${isSelf ? "is-self" : ""}`.trim()}>
                    <span className="collab-chip-identity">
                      <span className="collab-color-dot" style={{ backgroundColor: participant.color || "#1d4ed8" }} />
                      <span className="collab-participant-name">{participant.name || "Student"}</span>
                      {isSelf && <span className="collab-self-pill">You</span>}
                      {participant.userId === session?.ownerUserId && <span className="collab-host-pill">Host</span>}
                    </span>

                    <span
                      className={`collab-chip-voice ${participantVoiceState?.joined ? "" : "offline"} ${participantVoiceState?.muted ? "muted" : ""} ${participantSpeaking ? "speaking" : ""}`.trim()}
                      title={participantVoiceTitle}
                    >
                      <CollabHeaderIcon kind={participantVoiceState?.muted ? "mic-off" : "mic"} />
                      <VoiceMeterBars level={participantVoiceState?.joined ? participantVoiceLevel : 0} active={participantSpeaking} compact />
                    </span>

                    {isSelf && (
                      <span className="collab-chip-actions">
                        {!voiceJoined ? (
                          <button
                            className={`collab-chip-action ${voiceError ? "error" : ""}`.trim()}
                            type="button"
                            onClick={() => void joinVoiceSession()}
                            disabled={!isSecureVoiceContext || !canUseVoice || !roomSocketConnected}
                            title={voiceToggleLabel}
                            aria-label={voiceToggleLabel}
                          >
                            <CollabHeaderIcon kind="mic" />
                          </button>
                        ) : (
                          <>
                            <button
                              className={`collab-chip-action ${voiceMuted ? "" : "active"} ${voiceError ? "error" : ""}`.trim()}
                              type="button"
                              onClick={toggleVoiceMute}
                              title={voiceToggleLabel}
                              aria-label={voiceToggleLabel}
                            >
                              <CollabHeaderIcon kind={voiceToggleIcon} />
                            </button>
                            <button
                              className="collab-chip-action danger"
                              type="button"
                              onClick={() => leaveVoiceSession()}
                              title="Leave voice"
                              aria-label="Leave voice"
                            >
                              <CollabHeaderIcon kind="phone" />
                            </button>
                          </>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="collab-room-inline-actions">
              <button
                className="collab-header-icon-button"
                type="button"
                onClick={copyInviteLink}
                title="Copy room link"
                aria-label="Copy room link"
              >
                <CollabHeaderIcon kind="copy" />
              </button>

              <div
                className={`collab-room-status-chip ${roomSocketConnected ? "is-live" : ""} ${pendingCanvasOpsRef.current > 0 ? "is-syncing" : ""}`.trim()}
                title={roomStatusTitle}
              >
                <span className="collab-room-status-dot" />
                <span>{roomStatusLabel}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="error-text">{error}</div>}
      {notice && <div className="question-meta">{notice}</div>}

      <div className="question-block">
        <div className="question-title-row">
          <div className={`question-meta difficulty-label level-${String(question?.difficulty || session?.difficulty || "").toLowerCase()}`}>
            Level: {question?.difficulty || session?.difficulty || "Practice"}
          </div>
          {(frame?.title || question?.title) && <div className="question-title">{frame?.title || question?.title}</div>}
          {(question?.imageUrl || question?.uploadedImageUrl) && (
            <button
              type="button"
              className="secondary-button"
              onClick={() => setShowAttachmentModal(true)}
              style={{
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                padding: "4px 10px",
                fontWeight: "600"
              }}
              title="View original uploaded question image/PDF"
              aria-label="View original question image"
            >
              <span aria-hidden="true" style={{ fontSize: "14px" }}>🖼️</span>
              <span>Original Question</span>
            </button>
          )}
        </div>
        {questionLatex ? (
          <div className="practice-question-wrap">
            <BlockMath key={String(question?._id || question?.id || roomToken)} math={formatLatexForDisplay(questionLatex)} />
          </div>
        ) : (
          <div>No question loaded</div>
        )}
        {showQuestionFigure && (
          <div className="figure-block">
            <FigureRender figureSpec={questionFigure} cropToContent contentPadding={8} theme="accepted" />
          </div>
        )}
      </div>

      <div className="slate-card collaboration-room-slate">
        <div className="collab-toolbar collab-slate-toolbar">
          <div className="question-meta">Shared slate</div>
          <div className="break-time-launcher-wrap break-time-launcher-inline" ref={breakTimeMenuRef}>
            <button
              type="button"
              className={`break-time-launcher ${breakTimeOpen || breakTimeMenuOpen ? "active" : ""}`.trim()}
              onClick={() => setBreakTimeMenuOpen((prev) => !prev)}
              aria-label="Choose Break Time game"
              aria-expanded={breakTimeMenuOpen}
            >
              <span className="break-time-launcher-icon" aria-hidden="true">
                <CollabHeaderIcon kind="break" />
              </span>
              <span className="break-time-launcher-copy">
                <span className="break-time-launcher-label">Break Time</span>
                <span className="break-time-launcher-status">{breakTimeStatusLabel}</span>
              </span>
              <span className={`break-time-launcher-caret ${breakTimeMenuOpen ? "open" : ""}`.trim()} aria-hidden="true">
                {"\u2304"}
              </span>
            </button>

            {breakTimeMenuOpen && (
              <div className="break-time-menu" role="menu" aria-label="Break Time games">
                {BREAK_TIME_GAME_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`break-time-menu-item ${normalizedBreakTimeState.game === option.id ? "active" : ""}`.trim()}
                    onClick={() => handleOpenBreakTimeGame(option.id)}
                    role="menuitem"
                  >
                    <span className="break-time-menu-icon" aria-hidden="true">
                      {option.shortLabel}
                    </span>
                    <span className="break-time-menu-copy">
                      <span className="break-time-menu-label">{option.label}</span>
                      <span className="break-time-menu-description">{option.description}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <SlateCanvasPanel
          slateProfile={profile}
          slateFullScreen={slateFullScreen}
          strokeTool={strokeTool}
          showPenMenu={showPenMenu}
          strokeColor={strokeColor}
          lines={lines}
          redoStack={redoStack}
          liveOcrStatus={liveOcrStatus}
          isDrawing={isDrawing}
          showSlateDiagnostics={showSlateDiagnostics}
          stageCanvasWidth={stageCanvasWidth}
          stageSize={stageSize}
          laneBounds={laneBounds}
          remotePreviewLines={remotePreviewLines}
          laneDragBoundary={laneDragBoundary}
          slateRowDiagnostics={slateRowDiagnostics}
          selectedDiagStep={selectedDiagStep}
          toolbarMenuRef={toolbarMenuRef}
          slateRef={slateRef}
          canvasPaneRef={canvasPaneRef}
          stageRef={stageRef}
          activeStrokeCanvasRef={activeStrokeCanvasRef}
          diagnosticRailRef={diagnosticRailRef}
          setStrokeTool={setStrokeTool}
          setShowPenMenu={setShowPenMenu}
          setStrokeColor={setStrokeColor}
          handleUndoStroke={handleUndoStroke}
          handleRedoStroke={handleRedoStroke}
          setSlateFullScreen={setSlateFullScreen}
          handlePointerDown={handlePointerDown}
          handlePointerMove={handlePointerMove}
          handlePointerUp={handlePointerUp}
          handleRuleDragStart={handleRuleDragStart}
          setSelectedDiagStep={setSelectedDiagStep}
          analyzeActions={renderAnalyzeActions()}
          ruleStroke={RULE_STROKE}
          ruleStrokeWidth={RULE_STROKE_WIDTH}
          ruleDash={RULE_DASH}
          diagnosticRailWidth={DIAGNOSTIC_RAIL_WIDTH}
          onClearSlate={() => setShowClearConfirm(true)}
          clearSlateDisabled={isAnalyzing || isHinting}
          showClearConfirm={showClearConfirm}
          onCancelClearSlate={() => setShowClearConfirm(false)}
          onConfirmClearSlate={() => clearCanvas(true)}
          tutorLaneIndex={tutorLaneIndex}
          onTutorHintClick={handleRequestHint}
          onSlateScrollNearEnd={() => appendBlankSlateLanes(LANE_BATCH)}
          tutorHintDisabled={isAnalyzing || isHinting}
          isHinting={isHinting}
          hintingIntent={hintingIntent}
          tutorChatOpen={tutorChatOpen}
          onTutorChatToggle={() => setTutorChatOpen((prev) => !prev)}
          tutorHintData={tutorHintData}
          tutorHintHistory={tutorHintHistory}
          tutorModeMeta={tutorModeMeta}
          plainCanvas={true}
          stepClusters={stepClusters}
          paperStyle={paperStyle}
          setPaperStyle={handleSetPaperStyle}
          workTab={workTab}
          setWorkTab={setWorkTab}
          editorContent={
            <div className="slate-math-editor-inner">
              <div
                className="analysis-label-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px"
                }}
              >
                <div
                  className="analysis-label"
                  style={{ margin: 0, fontSize: "14px", fontWeight: "700" }}
                >
                  OCR Math Editor
                </div>
                <button
                  type="button"
                  className="math-keyboard-icon"
                  onClick={() => setShowMathKeyboard((prev) => !prev)}
                  title="Toggle math keyboard"
                >
                  {"\u2328"}
                </button>
              </div>
              {hasVisibleOcrLaneRows && (
                <div className="ocr-lane-review">
                  <div className="analysis-label">Slate OCR steps</div>
                  {visibleOcrLaneRows.map((row) => (
                    <div key={row.lineId} className={`ocr-lane-row ocr-lane-row-${row.contentKind}`}>
                      <div className="ocr-lane-row-head">
                        <span>Step {row.lineOrder + 1}</span>
                        <span>{getOcrLaneKindLabel(row.contentKind)}</span>
                        {row.confidence && <span>{row.confidence}</span>}
                      </div>
                      {row.latex && <div className="ocr-lane-latex">{row.latex}</div>}
                      {row.diagramSummaryText && (
                        <div className="ocr-lane-diagram-summary">{row.diagramSummaryText}</div>
                      )}
                      {!row.latex && !row.diagramSummaryText && (
                        <div className="analysis-text">This row is still unclear. Erase and redraw if needed.</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!ocrReady && !hasVisibleOcrLaneRows && (
                <div className="analysis-text">
                  OCR lines from the shared slate will appear here for optional editing.
                </div>
              )}
              {ocrReady && hasEditableOcrMath && (
                <>
                  <label>Review before Erwin analyzes</label>
                  <MathLiveEditor
                    value={ocrEditedLatex}
                    onChange={(nextValue) => updateOcrEditor(nextValue)}
                    showKeyboard={showMathKeyboard}
                    stepDiagnostics={stepDiagnostics}
                    selectedStep={selectedDiagStep}
                    onStepSelect={setSelectedDiagStep}
                  />
                </>
              )}
              {!hasEditableOcrMath && hasVisibleOcrLaneRows && (
                <div className="analysis-text">
                  No editable math line yet. Diagram facts will still be sent to tutor and analyze.
                </div>
              )}
            </div>
          }
        />
      </div>

      {analysisStatus && (
        <div className="analysis-box">
          <div className="question-meta">{analysisStatus}</div>
          {analysisData && (
            <div className="analysis-summary">
              {analysisFinalCorrect === true
                ? "Correct solution. See details below."
                : analysisFinalCorrect === false
                  ? "You have some improvement areas. See details below."
                  : analysisData.where_it_went_wrong_or_unclear || analysisData.next_step_suggestion
                    ? "You have some improvement areas. See details below."
                    : "Correct solution. See details below."}
            </div>
          )}
          {analysisData ? (
            <div className="analysis-grid">
              {analysisData.what_went_right && (
                <div>
                  <div className="analysis-label">What went right</div>
                  <div className="analysis-text">{analysisData.what_went_right}</div>
                </div>
              )}
              {analysisData.where_it_went_wrong_or_unclear && (
                <div>
                  <div className="analysis-label">Where it went wrong or unclear</div>
                  <div className="analysis-text">{analysisData.where_it_went_wrong_or_unclear}</div>
                </div>
              )}
              {analysisData.next_step_suggestion && (
                <div>
                  <div className="analysis-label">Next step suggestion</div>
                  <div className="analysis-text">{analysisData.next_step_suggestion}</div>
                </div>
              )}
            </div>
          ) : (
            analysisFeedback && <div className="analysis-text">{analysisFeedback}</div>
          )}
        </div>
      )}

      <BreakTimeTicTacToe
        open={breakTimeOpen}
        onClose={() => setBreakTimeOpen(false)}
        gameState={breakTimeState}
        currentUserId={currentUserId}
        ownerUserId={session?.ownerUserId}
        participants={session?.participants}
        onSyncState={syncBreakTimeState}
      />

      {showAttachmentModal && (question?.imageUrl || question?.uploadedImageUrl) && (
        <div
          className="modal-overlay"
          onClick={() => setShowAttachmentModal(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(15, 23, 42, 0.7)",
            zIndex: 1100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            backdropFilter: "blur(2px)"
          }}
        >
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "14px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
              maxWidth: "min(840px, calc(100vw - 32px))",
              maxHeight: "calc(100vh - 48px)",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden"
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 18px",
                borderBottom: "1px solid var(--border)",
                backgroundColor: "#f8fafc"
              }}
            >
              <div style={{ fontWeight: "700", fontSize: "14px", color: "var(--foreground)" }}>
                Original Question
              </div>
              <button
                type="button"
                onClick={() => setShowAttachmentModal(false)}
                style={{
                  backgroundColor: "#fee2e2",
                  border: "1.5px solid #ef4444",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: "700",
                  cursor: "pointer",
                  color: "#b91c1c",
                  padding: "5px 12px",
                  lineHeight: "1.2",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px"
                }}
                title="Close Window"
                aria-label="Close Window"
              >
                <span>✕</span>
                <span>Close</span>
              </button>
            </div>
            <div
              style={{
                padding: "16px",
                overflowY: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f1f5f9"
              }}
            >
              <img
                src={question.imageUrl || question.uploadedImageUrl}
                alt="Original Question"
                style={{
                  maxWidth: "100%",
                  maxHeight: "calc(100vh - 160px)",
                  borderRadius: "8px",
                  objectFit: "contain",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                }}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default CollaborationPractice;
